/**
 * render/hud.ts — In-Game HUD Overlay
 *
 * Every surface in this file should feel like it belongs in a warm, handcrafted
 * world. The visual language is aged wood + amber gold — warm panels, inset
 * slots, bevelled borders. No flat UI cards.
 *
 * Systems:
 *   • Bottom dock          — currency, quick-action buttons
 *   • Inventory panel      — tabbed grid, category icons, paginated
 *   • Placement banner     — shown while placing furniture
 *   • Placement direction  — compass rose showing current R-rotation
 *   • Context menu         — right-click placed furniture → Rotate / Move / Pick Up
 *
 * Hit zones are rebuilt every render frame. handleClick() consumes them in
 * the UPDATE phase before movement so clicks never accidentally walk the player.
 */

import type { GameState, Direction } from "../engine/state.js";
import { createStarterRoom, createTestZone } from "../engine/state.js";
import { getFurnitureType, renderFurniture, getFootprintTiles } from "../room/furniture.js";
import { startTravel, getZone } from "../world/zones.js";
import { addToast, drawToasts } from "./toasts.js";
import { QUESTS } from "../narrative/quests.js";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format large numbers compactly: 1000→1K, 1500000→1.5M */
function formatCompact(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "M";
  if (n >= 10_000) return (n / 1_000).toFixed(0) + "K";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

// ─── Layout ───────────────────────────────────────────────────────────────────

const BAR_H      = 72;
const BAR_PAD_X  = 12;
const BAR_PAD_Y  = 10;
const SLOT_SZ    = 46;
const SLOT_GAP   = 8;
const COMPASS_R  = 42;
const MINIMAP_R  = 65;
const PANEL_H    = 316;
const PANEL_GAP  = 5;
const CTX_W      = 148;
const CTX_ROW_H  = 36;

// ─── Colour Palette ───────────────────────────────────────────────────────────
// Warm aged-wood aesthetic.  All UI surfaces share this palette.

const W = {
  // panel surfaces
  woodDark:   "#1e1508",
  woodMid:    "#2a1d0e",
  woodLight:  "#342414",
  // amber borders
  goldDark:   "#6b4f0c",
  goldMid:    "#9b7318",
  goldBright: "#d4a030",
  goldGlow:   "#f0c860",
  // text
  textPrime:  "#f0d88a",
  textDim:    "#c0a060",
  textMuted:  "#7a6838",
  // item slot
  slotBg:     "#120e06",
  slotBorder: "#3a2810",
  // rarity
  rarCommon:   "#8a8070",
  rarUncommon: "#4a8050",
  rarRare:     "#406890",
  rarLegend:   "#7050a0",
  rarSeasonal: "#a06020",
  // context
  ctxBg:      "#1a1208",
  ctxHover:   "#2e2010",
  ctxRed:     "#9a3828",
  ctxRedText: "#e89070",
  // placement banner (warm amber, cohesive with wood palette)
  placeBg:    "#120e08",
  placeEdge:  "#8a6020",
  placeText:  "#e8c890",
  placeHint:  "#6a5030",
} as const;

const RARITY_COLORS: Record<string, string> = {
  common:    W.rarCommon,
  uncommon:  W.rarUncommon,
  rare:      W.rarRare,
  legendary: W.rarLegend,
  seasonal:  W.rarSeasonal,
};

const DIR_ANGLE: Record<string, number> = {
  E: 0, SE: Math.PI/4, S: Math.PI/2, SW: 3*Math.PI/4,
  W: Math.PI, NW: -3*Math.PI/4, N: -Math.PI/2, NE: -Math.PI/4,
};

const MINI_TILE: Record<string, string> = {
  wood: "#5a4020", stone: "#606068", carpet: "#6a3030",
  grass: "#305020", marble: "#989898", dirt: "#4a3820", water: "#203868",
  sand: "#c0a060", cobblestone: "#707070", dock: "#5a4830",
  stone_dark: "#3a3a40", snow: "#d0d8e0", lava: "#c04010", crystal: "#60a0c0",
};

// ─── Internal Types ───────────────────────────────────────────────────────────

interface HitZone {
  x: number; y: number; w: number; h: number;
  action: (state: GameState) => void;
}

// ─── Canvas Helpers ──────────────────────────────────────────────────────────

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y,     x + w, y + radius,     radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h,    x, y + h - radius,       radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y,        x + radius, y,            radius);
  ctx.closePath();
}

// ─── HudRenderer ─────────────────────────────────────────────────────────────

export class HudRenderer {
  private ctx: CanvasRenderingContext2D;
  private hitZones: HitZone[] = [];
  private roomHitZones: HitZone[] = [];
  private tooltipZones: Array<{ x: number; y: number; w: number; h: number; text: string }> = [];
  private invPage = 0;  // current inventory page (for pagination)
  private travelHomeRect: { x: number; y: number; w: number; h: number } | null = null;
  private testZoneRect: { x: number; y: number; w: number; h: number } | null = null;
  /** Current mouse position for hover detection */
  mouseX = 0;
  mouseY = 0;
  /** Pointer down state for drag interactions */
  mouseDown = false;
  /** Zone banner entrance animation tracking */
  private lastZoneName = "";
  private zoneEntryTime = 0;
  /** Click ripple effect — expanding gold ring on button press */
  private clickRipples: Array<{ x: number; y: number; time: number }> = [];
  /** Slot press animation: track last clicked slot center for scale bounce */
  private _slotPressX = -1;
  private _slotPressY = -1;
  private _slotPressTime = 0;
  /** Inventory panel slide-up/fade animation */
  private invOpenTime = 0;
  /** Context menu scale-up/fade-in animation */
  private ctxMenuOpenTime = 0;
  private ctxMenuWasOpen = false;
  /** Placement banner slide-down entrance animation */
  private placementBannerOpenTime = 0;
  private placementBannerWasOpen = false;
  private _placementBannerExitTime = 0;
  /** Edit mode banner animation */
  private _editBannerOpenTime = 0;
  private _editBannerExitTime = 0;
  private _editBannerWasOpen = false;
  /** Combat stance banner animation */
  private _combatBannerOpenTime = 0;
  private _combatBannerExitTime = 0;
  private _combatBannerWasOpen = false;
  private invCloseTime = 0;
  private invWasOpen = false;
  /** Tooltip fade tracking */
  private _tooltipOpenTime = 0;
  private _tooltipLastText = "";
  private _tooltipExitTime = 0;
  private _tooltipPrevText = "";

  constructor(_canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
    // Track mouse for hover states
    _canvas.addEventListener("mousemove", (e) => {
      const rect = _canvas.getBoundingClientRect();
      const scaleX = _canvas.width / rect.width;
      const scaleY = _canvas.height / rect.height;
      this.mouseX = (e.clientX - rect.left) * scaleX;
      this.mouseY = (e.clientY - rect.top) * scaleY;
    });
    _canvas.addEventListener("pointerdown", () => { this.mouseDown = true; });
    _canvas.addEventListener("pointerup", () => { this.mouseDown = false; });
    _canvas.addEventListener("pointerleave", () => { this.mouseDown = false; });
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  handleClick(x: number, y: number, state: GameState): boolean {
    // Room hit zones first (toolbar buttons take priority)
    for (const z of this.roomHitZones) {
      if (x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h) {
        z.action(state);
        this.clickRipples.push({ x, y, time: Date.now() });
        return true;
      }
    }

    // Travel home button
    const th = this.travelHomeRect;
    if (th && x >= th.x && x <= th.x + th.w && y >= th.y && y <= th.y + th.h) {
      if (!state.world.travelState.active && state.world.currentZoneId !== "ember_lane") {
        startTravel(state.world, "ember_lane");
        state.ui.uiSounds.push("ui_open");
      }
      this.clickRipples.push({ x, y, time: Date.now() });
      return true;
    }

    // Test zone button
    const tz = this.testZoneRect;
    if (tz && x >= tz.x && x <= tz.x + tz.w && y >= tz.y && y <= tz.y + tz.h) {
      const isTestZone = state.currentRoom?.id === "room_test_zone";
      if (isTestZone) {
        state.currentRoom = createStarterRoom(state.player.id);
        state.player.position = { tileX: 3, tileY: 3, elevation: 0 };
        addToast(state, "Back to your room", "info", 2000);
      } else {
        state.currentRoom = createTestZone(state.player.id);
        state.player.position = { tileX: 10, tileY: 16, elevation: 0 };
        addToast(state, "Asset Test Zone", "info", 2000);
      }
      state.ui.uiSounds.push("ui_open");
      this.clickRipples.push({ x, y, time: Date.now() });
      return true;
    }

    for (const z of this.hitZones) {
      if (x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h) {
        z.action(state);
        this.clickRipples.push({ x, y, time: Date.now() });
        // Generic click sound — specific sounds (ui_open, ui_equip) override
        if (state.ui.uiSounds.length === 0) state.ui.uiSounds.push("ui_click");
        return true;
      }
    }
    return false;
  }

  /** Scroll inventory pages. delta > 0 = next page, delta < 0 = prev page. */
  scrollInventory(delta: number, state: GameState): void {
    if (!state.ui.inventoryOpen) return;
    const items = state.player.inventory.filter(it => {
      if (state.ui.inventoryTab === "all") return true;
      return it.type === state.ui.inventoryTab;
    });
    const COLS = 5, maxRows = 4, perPage = COLS * maxRows;
    const pages = Math.max(1, Math.ceil(items.length / perPage));
    if (pages <= 1) return;
    if (delta > 0) this.invPage = (this.invPage + 1) % pages;
    else this.invPage = (this.invPage - 1 + pages) % pages;
    state.ui.uiSounds.push("ui_tab");
  }

  /** Returns true if mouse is currently over any clickable HUD element. */
  isMouseOverClickable(): boolean {
    const x = this.mouseX, y = this.mouseY;
    // Check travel home button
    const th = this.travelHomeRect;
    if (th && x >= th.x && x <= th.x + th.w && y >= th.y && y <= th.y + th.h) return true;
    // Check test zone button
    const tz = this.testZoneRect;
    if (tz && x >= tz.x && x <= tz.x + tz.w && y >= tz.y && y <= tz.y + tz.h) return true;
    // Check room zones (furniture toolbar etc.)
    for (const z of this.roomHitZones) {
      if (x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h) return true;
    }
    // Check all HUD zones
    for (const z of this.hitZones) {
      if (x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h) return true;
    }
    return false;
  }

  /**
   * Called at the start of each render frame to clear room hit zones.
   * Room zones are registered during overlay rendering (before hud.draw),
   * so they must be cleared here — not in draw() — to survive for the next frame.
   */
  beginFrame(): void {
    this.roomHitZones = [];
  }

  draw(_ts: number, state: GameState, SW: number, SH: number): void {
    this.hitZones = [];
    this.tooltipZones = [];
    const now = Date.now();

    // ── Edit mode banner (when not in placement sub-mode) ──
    {
      const showEditBanner = state.ui.editMode && !state.ui.placingFurnitureType && !state.ui.movingFurnitureId;
      if (showEditBanner && !this._editBannerWasOpen) this._editBannerOpenTime = now;
      if (!showEditBanner && this._editBannerWasOpen) this._editBannerExitTime = now;
      this._editBannerWasOpen = showEditBanner;
      // Entrance or exit animation
      const editBannerAlpha = showEditBanner
        ? Math.min(1, (now - this._editBannerOpenTime) / 200)
        : this._editBannerExitTime
          ? 1 - Math.min(1, (now - this._editBannerExitTime) / 150)
          : 0;
      if (editBannerAlpha > 0.01) {
        const ease = showEditBanner
          ? 1 - Math.pow(1 - editBannerAlpha, 3)
          : editBannerAlpha;
        const { ctx } = this;
        ctx.save();
        ctx.globalAlpha = ease;
        ctx.translate(0, -30 * (1 - ease));
        this.drawEditModeBanner(state, SW);
        ctx.restore();
      } else if (!showEditBanner) {
        this._editBannerExitTime = 0;
      }
    }

    // ── Combat stance banner ──
    {
      const showCombatBanner = state.ui.combatStance && !state.player.combatState.active;
      if (showCombatBanner && !this._combatBannerWasOpen) this._combatBannerOpenTime = now;
      if (!showCombatBanner && this._combatBannerWasOpen) this._combatBannerExitTime = now;
      this._combatBannerWasOpen = showCombatBanner;
      const combatBannerAlpha = showCombatBanner
        ? Math.min(1, (now - this._combatBannerOpenTime) / 200)
        : this._combatBannerExitTime
          ? 1 - Math.min(1, (now - this._combatBannerExitTime) / 150)
          : 0;
      if (combatBannerAlpha > 0.01) {
        const ease = showCombatBanner
          ? 1 - Math.pow(1 - combatBannerAlpha, 3)
          : combatBannerAlpha;
        const { ctx } = this;
        ctx.save();
        ctx.globalAlpha = ease;
        ctx.translate(0, -30 * (1 - ease));
        this.drawCombatStanceBanner(state, SW);
        ctx.restore();
      } else if (!showCombatBanner) {
        this._combatBannerExitTime = 0;
      }
    }

    // ── Placement banner ──
    {
      const placing = !!(state.ui.placingFurnitureType || state.ui.movingFurnitureId);
      if (placing && !this.placementBannerWasOpen) this.placementBannerOpenTime = now;
      if (!placing && this.placementBannerWasOpen) this._placementBannerExitTime = now;
      this.placementBannerWasOpen = placing;
      const { ctx } = this;
      if (placing) {
        const elapsed = now - this.placementBannerOpenTime;
        const PLACE_ANIM_DUR = 250;
        const t = Math.min(1, elapsed / PLACE_ANIM_DUR);
        if (t < 1) {
          const ease = 1 - Math.pow(1 - t, 3);
          ctx.save();
          ctx.globalAlpha = ease;
          ctx.translate(0, -30 * (1 - ease)); // slides down from above
          this.drawPlacementBanner(state, SW);
          ctx.restore();
        } else {
          this.drawPlacementBanner(state, SW);
        }
      } else if (this._placementBannerExitTime) {
        // Fade-out exit animation (150ms)
        const exitAlpha = 1 - Math.min(1, (now - this._placementBannerExitTime) / 150);
        if (exitAlpha > 0.01) {
          ctx.save();
          ctx.globalAlpha = exitAlpha;
          this.drawPlacementBanner(state, SW);
          ctx.restore();
        } else {
          this._placementBannerExitTime = 0;
        }
      }
    }

    this.drawCompass(state, SW);
    this.drawMinimap(state, SW);
    // Quest tracker is drawn by quest_ui.ts drawQuestTracker() in the render pipeline
    this.drawActionBar(state, SW, SH);

    // ── Inventory panel with slide-up / fade animation ──
    {
      const isOpen = state.ui.inventoryOpen;

      // Detect open transition (false→true)
      if (isOpen && !this.invWasOpen) {
        this.invOpenTime = now;
        this.invCloseTime = 0;  // cancel any in-progress close
      }
      // Detect close transition (true→false)
      if (!isOpen && this.invWasOpen) {
        this.invCloseTime = now;
      }
      this.invWasOpen = isOpen;

      const OPEN_DUR = 300;   // ms
      const CLOSE_DUR = 200;  // ms
      const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

      // Determine if we should draw (open, or close animation still playing)
      const closingAnimActive = !isOpen && this.invCloseTime > 0 && (now - this.invCloseTime) < CLOSE_DUR;

      if (isOpen || closingAnimActive) {
        let alpha = 1;
        let yOffset = 0;
        let animating = false;

        if (isOpen) {
          // Opening animation
          const elapsed = now - this.invOpenTime;
          if (elapsed < OPEN_DUR) {
            const t = easeOut(Math.min(1, elapsed / OPEN_DUR));
            alpha = t;
            yOffset = 30 * (1 - t);  // slides up from 30px below
            animating = true;
          }
        } else {
          // Closing animation — always animating (panel closing, no hit zones)
          const elapsed = now - this.invCloseTime;
          const t = easeOut(Math.min(1, elapsed / CLOSE_DUR));
          alpha = 1 - t;
          yOffset = 30 * t;  // slides down 30px
          animating = true;
        }

        const { ctx } = this;
        if (animating) {
          // During animation: render with transform but suppress hit zone registration
          const savedHits = this.hitZones;
          this.hitZones = [];
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.translate(0, yOffset);
          this.drawInventoryPanel(state, SW, SH);
          ctx.restore();
          this.hitZones = savedHits; // discard misaligned zones
        } else {
          // Animation complete: render normally with valid hit zones
          this.drawInventoryPanel(state, SW, SH);
        }
      }
    }

    {
      const ctxOpen = !!state.ui.furnitureContextId;
      if (ctxOpen && !this.ctxMenuWasOpen) this.ctxMenuOpenTime = now;
      this.ctxMenuWasOpen = ctxOpen;
      if (ctxOpen) {
        const elapsed = now - this.ctxMenuOpenTime;
        const CTX_ANIM_DUR = 180;
        const t = Math.min(1, elapsed / CTX_ANIM_DUR);
        const { ctx } = this;
        if (t < 1) {
          // During animation: render with transform, suppress hit zones
          const ease = 1 - Math.pow(1 - t, 3);
          const scl = 0.85 + 0.15 * ease;
          const cmx = state.ui.furnitureContextX;
          const cmy = state.ui.furnitureContextY;
          const savedHits = this.hitZones;
          this.hitZones = [];
          ctx.save();
          ctx.globalAlpha = ease;
          ctx.translate(cmx * (1 - scl), cmy * (1 - scl));
          ctx.scale(scl, scl);
          this.drawContextMenu(state, SW, SH);
          ctx.restore();
          this.hitZones = savedHits;
        } else {
          // Animation complete: render normally with valid hit zones
          this.drawContextMenu(state, SW, SH);
        }
      }
    }

    // Zone banner + travel-home button + test zone button (only in room scene)
    this.drawZoneBanner(state, SW);
    this.drawTravelHomeButton(state, SW);
    this.drawTestZoneButton(state, SW);

    // ── Ambient HUD tint — the UI breathes with the world ──
    this.drawAmbientTint(state, SW, SH);

    // ── Click ripple effects — expanding gold rings on button press ──
    this.drawClickRipples();

    // ── Full-screen room map overlay ──
    if (state.ui.mapOpen) {
      this.drawFullMap(state, SW, SH);
    }

    // Tooltips — drawn last, on top of everything
    this.drawTooltips(SW);

    // Toast notifications — bottom-right stack, on top of everything else
    drawToasts(this.ctx, state, SW, SH, _ts);
  }

  // ─── Action Bar ──────────────────────────────────────────────────────────────

  private drawActionBar(state: GameState, SW: number, SH: number): void {
    const { ctx } = this;
    const barW = SW - BAR_PAD_X * 2;
    const barX = BAR_PAD_X;
    const barY = SH - BAR_H - BAR_PAD_Y;
    const compact = SW < 520; // responsive: collapse left/right sections on narrow screens

    // Drop shadow above bar
    ctx.save();
    const shG = ctx.createLinearGradient(barX, barY - 14, barX, barY);
    shG.addColorStop(0, "transparent");
    shG.addColorStop(1, "rgba(0,0,0,0.35)");
    ctx.fillStyle = shG;
    ctx.fillRect(barX, barY - 14, barW, 14);
    ctx.restore();

    // Full-width wood panel
    this.drawWoodPanel(barX, barY, barW, BAR_H, 10);

    // Mode accent strip — thin colored glow at the top edge of the bar
    if (state.ui.editMode || state.ui.combatStance) {
      ctx.save();
      const accentColor = state.ui.editMode ? "rgba(58, 123, 213, 0.6)" : "rgba(213, 58, 58, 0.6)";
      const accentGrad = ctx.createLinearGradient(barX, barY, barX, barY + 8);
      accentGrad.addColorStop(0, accentColor);
      accentGrad.addColorStop(1, "transparent");
      ctx.fillStyle = accentGrad;
      roundRect(ctx, barX, barY, barW, 8, 10);
      ctx.fill();
      ctx.restore();
    }

    // Ornamental corner brackets (top-left and top-right)
    this.drawCornerBracket(barX + 4, barY + 4, false);
    this.drawCornerBracket(barX + barW - 4, barY + 4, true);

    // ── Left section: Player identity ──
    const midY = barY + BAR_H / 2;
    if (compact) {
      // Compact mode: just the crest, no text stats
      this.drawCrest(barX + 10, midY - 16, 28, 32, state);
    } else {
      this.drawCrest(barX + 14, midY - 20, 36, 40, state);

      // Player name + level badge
      ctx.save();
      ctx.font = "bold 12px monospace";
      ctx.fillStyle = W.textPrime;
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      // Truncate long names to fit left section
      const maxNameW = 80;
      let displayName = state.player.name;
      if (ctx.measureText(displayName).width > maxNameW) {
        while (displayName.length > 3 && ctx.measureText(displayName + "…").width > maxNameW) displayName = displayName.slice(0, -1);
        displayName += "…";
      }
      ctx.fillText(displayName, barX + 58, midY - 12);
      // Level shield — small gold badge after name
      const nameW = ctx.measureText(displayName).width;
      const lvlX = barX + 58 + nameW + 8;
      const lvlY = midY - 12;
      const lvl = state.player.combat.level;
      // Shield shape
      ctx.beginPath();
      ctx.moveTo(lvlX, lvlY - 8);
      ctx.lineTo(lvlX + 10, lvlY - 8);
      ctx.lineTo(lvlX + 10, lvlY + 2);
      ctx.lineTo(lvlX + 5, lvlY + 7);
      ctx.lineTo(lvlX, lvlY + 2);
      ctx.closePath();
      ctx.fillStyle = W.goldDark;
      ctx.fill();
      ctx.strokeStyle = W.goldBright;
      ctx.lineWidth = 1;
      ctx.stroke();
      // Level number centered in shield
      ctx.font = "bold 8px monospace";
      ctx.fillStyle = W.textPrime;
      ctx.textAlign = "center";
      ctx.fillText(`${lvl}`, lvlX + 5, lvlY - 1);
      // XP progress bar — thin amber bar below level shield
      const xpBarX = barX + 58;
      const xpBarY = lvlY + 10;
      const xpBarW = lvlX - xpBarX + 12;
      const xpBarH = 3;
      this.rr(xpBarX, xpBarY, xpBarW, xpBarH, 1.5);
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.fill();
      const xpRatio = state.player.combat.xpToNext > 0 ? state.player.combat.xp / state.player.combat.xpToNext : 0;
      if (xpRatio > 0) {
        this.rr(xpBarX, xpBarY, Math.max(2, xpBarW * xpRatio), xpBarH, 1.5);
        ctx.fillStyle = W.goldMid;
        ctx.fill();
      }
      ctx.restore();

      // Currency badge
      const cX = barX + 58;
      const cY = midY + 5;
      ctx.save();
      ctx.fillStyle = W.goldBright;
      ctx.beginPath(); ctx.arc(cX + 6, cY, 6, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = W.goldDark;
      ctx.beginPath(); ctx.arc(cX + 6, cY, 3.5, 0, Math.PI * 2); ctx.fill();
      ctx.font = "bold 10px monospace";
      ctx.fillStyle = W.goldGlow;
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      ctx.fillText(formatCompact(state.player.currency), cX + 16, cY);
      ctx.restore();

      // Reputation star
      const rX = cX + 58;
      ctx.save();
      this.drawStar5(rX, cY, 6, W.goldBright);
      ctx.font = "bold 10px monospace";
      ctx.fillStyle = W.textDim;
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      ctx.fillText(`${state.player.reputation}`, rX + 10, cY);
      ctx.restore();

      // ── HP / EP micro-bars ──
      const { hp, maxHp, ember } = state.player.combat;
      const hpBarX = barX + 58;
      const hpBarY = cY + 10;
      const hpBarW = 72;
      const hpBarH = 4;
      // HP bar
      this.rr(hpBarX, hpBarY, hpBarW, hpBarH, 2);
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.fill();
      const hpRatio = maxHp > 0 ? hp / maxHp : 1;
      const hpColor = hpRatio > 0.5 ? "#50b040" : hpRatio > 0.25 ? "#c0a030" : "#c03030";
      if (hpRatio > 0) {
        this.rr(hpBarX, hpBarY, Math.max(2, hpBarW * hpRatio), hpBarH, 2);
        ctx.fillStyle = hpColor;
        ctx.fill();
      }
      // EP bar (below HP)
      const epBarY = hpBarY + hpBarH + 2;
      this.rr(hpBarX, epBarY, hpBarW, hpBarH, 2);
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.fill();
      const epRatio = ember.max > 0 ? ember.current / ember.max : 1;
      if (epRatio > 0) {
        this.rr(hpBarX, epBarY, Math.max(2, hpBarW * epRatio), hpBarH, 2);
        ctx.fillStyle = "#4080c0";
        ctx.fill();
      }

      // ── Divider 1 ──
      const div1X = barX + 196;
      this.drawGoldDivider(div1X, barY + 8, barY + BAR_H - 8);
    }

    // ── Center: Action buttons ──
    type Slot = { icon: string; label: string; key: string; active: boolean; disabled?: boolean; tooltip: string; badge: number; badgeStyle: "notify" | "count"; pulse: boolean; action: (s: GameState) => void };
    // Total unread device notifications — shown as badge on device button
    const deviceBadgeCount = state.player.device.notifications.reduce((sum, n) => sum + n.count, 0);
    // Inventory item count for Bag slot badge
    const invItemCount = state.player.inventory.reduce((sum, it) => sum + it.quantity, 0);
    // Unread chat: active bubbles from someone other than the player
    const hasUnreadChat = state.chat.activeBubbles.some(
      (b) => b.senderId !== state.player.name && b.until > Date.now()
    );

    // ── Shared buttons ──
    const bagSlot: Slot = {
      icon: "backpack", label: "Bag", key: "I",
      active: state.ui.inventoryOpen, badge: invItemCount > 0 ? invItemCount : 0, badgeStyle: "count", pulse: false,
      tooltip: "Inventory  [I]\nYour items, furniture, and equipment",
      action: (s) => {
        s.ui.inventoryOpen = !s.ui.inventoryOpen;
        if (s.ui.inventoryOpen) { s.ui.aeternaOpen = false; s.ui.uiSounds.push("ui_open"); }
        else { s.ui.uiSounds.push("ui_close"); }
      },
    };
    const chatSlot: Slot = {
      icon: "bubble", label: "Chat", key: "↵",
      active: state.chat.inputOpen, badge: 0, badgeStyle: "notify", pulse: hasUnreadChat,
      tooltip: "Chat  [Enter]\nTalk to people nearby",
      action: () => { /* handled by Enter key */ },
    };

    const hasSel = !!state.ui.selectedFurniture;

    const slots: Slot[] = state.ui.editMode ? [
      // ── Build mode button set ──
      bagSlot,
      {
        icon: "rotate", label: "Rotate", key: "R",
        active: false, disabled: !hasSel, badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: hasSel ? "Rotate  [R]\nRotate selected furniture" : "Rotate  [R]\nSelect furniture first",
        action: (s) => { if (s.ui.selectedFurniture) s.ui.pendingBuildAction = "rotate"; },
      },
      {
        icon: "move", label: "Move", key: "M",
        active: !!state.ui.movingFurnitureId, disabled: !hasSel, badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: hasSel ? "Move  [M]\nPick up and reposition" : "Move  [M]\nSelect furniture first",
        action: (s) => { if (s.ui.selectedFurniture) s.ui.pendingBuildAction = "move"; },
      },
      {
        icon: "hand", label: "Pick Up", key: "G",
        active: false, disabled: !hasSel, badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: hasSel ? "Pick Up  [G]\nReturn to inventory" : "Pick Up  [G]\nSelect furniture first",
        action: (s) => { if (s.ui.selectedFurniture) s.ui.pendingBuildAction = "pickup"; },
      },
      chatSlot,
      {
        icon: "check", label: "Done", key: "P",
        active: false, badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: "Exit Build Mode  [P]\nReturn to normal",
        action: (s) => {
          s.ui.editMode = false;
          s.ui.selectedFurniture = null;
          s.ui.furnitureContextId = null;
          s.ui.placingFurnitureType = null;
          s.ui.movingFurnitureId = null;
          s.ui.inventoryOpen = false;
        },
      },
    ] : [
      // ── Normal / combat button set ──
      bagSlot,
      {
        icon: "mirror", label: "Look", key: "L",
        active: state.ui.deviceOpen && state.player.device.activeApp === "wardrobe", badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: "Look Mode  [L]\nExamine objects in the room",
        action: (s) => {
          if (s.ui.deviceOpen && s.player.device.activeApp === "wardrobe") {
            s.ui.deviceToggleRequest = s.player.device.activeDevice ?? "phone";
          } else {
            s.ui.deviceToggleRequest = "phone";
            s.ui.deviceOpenToApp = "wardrobe";
          }
        },
      },
      {
        icon: "hammer", label: "Build", key: "P",
        active: false, badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: "Build Mode  [P]\nRearrange your furniture",
        action: (s) => {
          s.ui.editMode = true;
          s.ui.combatStance = false;
          s.ui.inventoryOpen = true;
          s.ui.inventoryTab  = "furniture";
          s.ui.aeternaOpen   = false;
          // Close device if open — can't build while on phone
          if (s.ui.deviceOpen) {
            s.ui.deviceOpen = false;
            s.ui.deviceScreenRect = null;
          }
        },
      },
      {
        icon: "sword", label: state.ui.combatStance ? "Sheathe" : "Fight", key: "C",
        active: state.ui.combatStance, badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: state.ui.combatStance
          ? "Sheathe Weapon  [C]\nReturn to peaceful stance"
          : "Draw Weapon  [C]\nEnter combat stance",
        action: (s) => {
          if (s.ui.editMode) return; // can't fight in build mode
          if (s.ui.combatStance) {
            s.ui.combatStance = false;
            s.player.animState = "idle";
          } else {
            s.ui.combatStance = true;
            if (s.player.animState === "sit" || s.player.animState === "device_sit" || s.player.animState === "sleep") {
              s.player.position.elevation = 0;
            }
            // Close device if open — can't hold phone while fighting
            if (s.ui.deviceOpen) {
              s.ui.deviceOpen = false;
              s.ui.deviceScreenRect = null;
            }
            s.player.animState = "combat_idle";
            s.player.targetPosition = undefined;
            s.player.movementQueue = [];
          }
        },
      },
      {
        icon: "emote", label: "Emote", key: "E",
        active: state.player.emoteWheel.open, badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: "Emote Wheel  [E]\nExpress yourself",
        action: (s) => {
          if (s.player.combatState.active || s.ui.editMode) return;
          s.player.emoteWheel.open = !s.player.emoteWheel.open;
          if (s.player.emoteWheel.open) { s.player.emoteWheel.openedAt = Date.now(); s.player.emoteWheel.selectedIndex = -1; }
        },
      },
      chatSlot,
      {
        icon: "eye", label: "Aeterna", key: "A",
        active: state.ui.aeternaOpen || (state.ui.deviceOpen && state.player.device.activeApp === "hearth"),
        badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: "Companion  [A]\nOpen your Aeterna companion · Enter to speak",
        action: (s) => {
          const alreadyOpen = s.ui.deviceOpen && s.player.device.activeApp === "hearth";
          if (alreadyOpen) {
            // Close device — toggle off
            s.ui.deviceToggleRequest = s.player.device.activeDevice ?? "phone";
            s.ui.aeternaOpen = false;
          } else {
            s.ui.inventoryOpen = false;
            s.ui.aeternaOpen = true;
            s.ui.deviceToggleRequest = "phone";
            s.ui.deviceOpenToApp = "hearth";
          }
        },
      },
      {
        icon: "phone", label: "Device", key: "D",
        active: state.ui.deviceOpen, badge: deviceBadgeCount, badgeStyle: "notify", pulse: false,
        tooltip: "Device  [D]\nPull out your hPhone · Shift+D for hPad",
        action: (s) => {
          if (s.ui.deviceOpen && s.player.device.activeDevice === "phone") {
            s.ui.deviceToggleRequest = "pad";
          } else {
            s.ui.deviceToggleRequest = "phone";
          }
        },
      },
      {
        icon: "gear", label: "Settings", key: "",
        active: state.ui.deviceOpen && state.player.device.activeApp === "settings", badge: 0, badgeStyle: "notify", pulse: false,
        tooltip: "Settings\nPreferences and customization\nOpens in hPhone",
        action: (s) => {
          if (s.ui.deviceOpen && s.player.device.activeApp === "settings") {
            s.ui.deviceToggleRequest = s.player.device.activeDevice ?? "phone";
          } else {
            s.ui.deviceToggleRequest = "phone";
            s.ui.deviceOpenToApp = "settings";
          }
        },
      },
    ];

    // ── Responsive slot sizing ──
    // Compute how much horizontal space is available for slots between left and right sections
    const leftSectionW = compact ? 60 : 196;   // compact: just crest, no text stats
    const rightSectionW = compact ? 60 : 148;  // compact: minimal time
    const slotsAreaW = barW - leftSectionW - rightSectionW - 16; // 16px breathing room
    const idealTotalW = slots.length * SLOT_SZ + (slots.length - 1) * SLOT_GAP;
    // Scale down slot size if they don't fit
    const slotScale = Math.min(1, slotsAreaW / idealTotalW);
    const eSz = Math.max(28, Math.round(SLOT_SZ * slotScale));   // effective slot size, floor 28px
    const eGap = Math.max(3, Math.round(SLOT_GAP * slotScale));  // effective gap, floor 3px
    const totalSlotsW = slots.length * eSz + (slots.length - 1) * eGap;
    const slotsX0 = barX + leftSectionW + (slotsAreaW - totalSlotsW) / 2 + 8;
    const slotY = barY + (compact ? 8 : 6);

    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      const sx = slotsX0 + i * (eSz + eGap);
      const isHovered = this.mouseX >= sx && this.mouseX <= sx + eSz &&
                        this.mouseY >= slotY && this.mouseY <= slotY + eSz + 14;
      // Chat pulse — gentle amber glow animation when unread messages exist
      if (slot.pulse && !slot.active) {
        const pulseAlpha = 0.06 + Math.sin(Date.now() * 0.003) * 0.06;
        ctx.save();
        const pcx = sx + eSz / 2, pcy = slotY + eSz / 2;
        const pg = ctx.createRadialGradient(pcx, pcy, eSz * 0.1, pcx, pcy, eSz * 0.65);
        pg.addColorStop(0, `rgba(240, 200, 96, ${pulseAlpha})`);
        pg.addColorStop(1, "rgba(240, 200, 96, 0)");
        ctx.fillStyle = pg;
        ctx.fillRect(sx - 4, slotY - 4, eSz + 8, eSz + 8);
        ctx.restore();
      }
      this.drawActionSlot(sx, slotY, slot.icon, slot.label, slot.key, slot.active, isHovered, eSz, slot.disabled);
      // Badge rendering — red notification pill or amber item count
      if (slot.badge > 0) {
        if (slot.badgeStyle === "count") {
          this.drawSlotCountBadge(sx + eSz - 4, slotY + eSz - 6, slot.badge);
        } else {
          this.drawSlotBadge(sx + eSz - 4, slotY + 2, slot.badge, state.time.gameHour);
        }
      }
      const slotCx = sx + eSz / 2, slotCy = slotY + eSz / 2;
      const sa = slot.action;
      const wrappedAction = (s: GameState) => {
        this._slotPressX = slotCx;
        this._slotPressY = slotCy;
        this._slotPressTime = Date.now();
        sa(s);
      };
      this.addHit(sx, slotY, eSz, eSz + 14, wrappedAction);
      if (isHovered && slot.tooltip) {
        this.tooltipZones.push({ x: sx, y: slotY, w: eSz, h: eSz + 14, text: slot.tooltip });
      }
    }

    // ── Divider 2 ── (skip on compact layouts)
    if (!compact) {
      const div2X = barX + barW - 148;
      this.drawGoldDivider(div2X, barY + 8, barY + BAR_H - 8);
    }

    // ── Right: Time display ──
    if (compact) {
      // Compact time — just the clock, no date or season ornament
      this.drawCompactTime(barX + barW - 56, barY, 48, BAR_H, state);
    } else {
      this.drawTimeSection(barX + barW - 138, barY, 126, BAR_H, state);
    }
  }

  /** Single action button: sunken slot + procedural icon + label + key hint */
  private drawActionSlot(
    x: number, y: number,
    icon: string, label: string, key: string, active: boolean, hovered = false,
    sz: number = SLOT_SZ, disabled?: boolean
  ): void {
    const { ctx } = this;
    const lit = (active || hovered) && !disabled;
    const iconSz = Math.round(sz * 0.48); // icon scales with slot

    // Disabled overlay — dim the entire slot
    if (disabled) {
      ctx.save();
      ctx.globalAlpha = 0.4;
    }

    // Button press scale animation — 120ms bounce when clicked
    const scx = x + sz / 2, scy = y + sz / 2;
    const pressDist = Math.abs(scx - this._slotPressX) + Math.abs(scy - this._slotPressY);
    const pressAge = Date.now() - this._slotPressTime;
    let pressScale = 1;
    if (pressDist < sz && pressAge < 120) {
      const t = pressAge / 120;
      pressScale = 1 - 0.08 * Math.sin(t * Math.PI); // dip to 0.92 then back to 1.0
    }
    if (pressScale !== 1) {
      ctx.save();
      ctx.translate(scx, scy);
      ctx.scale(pressScale, pressScale);
      ctx.translate(-scx, -scy);
    }

    // Warm amber glow behind slot on hover (radial, extends 4px beyond bounds)
    if (hovered && !active) {
      ctx.save();
      const gcx = x + sz / 2, gcy = y + sz / 2;
      const glow = ctx.createRadialGradient(gcx, gcy, sz * 0.2, gcx, gcy, sz * 0.7 + 4);
      glow.addColorStop(0, "rgba(240, 200, 96, 0.15)");
      glow.addColorStop(1, "rgba(240, 200, 96, 0)");
      ctx.fillStyle = glow;
      ctx.fillRect(x - 4, y - 4, sz + 8, sz + 8);
      ctx.restore();
    }

    // Active slot: permanent golden glow (stronger, always visible)
    if (active) {
      ctx.save();
      const gcx = x + sz / 2, gcy = y + sz / 2;
      const glow = ctx.createRadialGradient(gcx, gcy, sz * 0.15, gcx, gcy, sz * 0.75 + 4);
      glow.addColorStop(0, "rgba(240, 200, 96, 0.22)");
      glow.addColorStop(0.6, "rgba(212, 160, 48, 0.10)");
      glow.addColorStop(1, "rgba(212, 160, 48, 0)");
      ctx.fillStyle = glow;
      ctx.fillRect(x - 4, y - 4, sz + 8, sz + 8);
      ctx.restore();
    }

    // Sunken slot background with inset bevel
    ctx.save();
    this.rr(x, y, sz, sz, 6);
    ctx.fillStyle = active ? "#2a2008" : hovered ? "#221a0c" : W.slotBg;
    ctx.fill();
    ctx.strokeStyle = active ? W.goldBright : hovered ? W.goldBright : "rgba(0,0,0,0.6)";
    ctx.lineWidth = active ? 1.5 : hovered ? 1.5 : 1;
    ctx.stroke();
    // Inset shadow (top-left darker = pressed into wood)
    this.rr(x + 1, y + 1, sz - 2, sz - 2, 5);
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
    // Inset highlight (bottom-right catches light)
    ctx.beginPath();
    ctx.moveTo(x + 6, y + sz - 1);
    ctx.lineTo(x + sz - 6, y + sz - 1);
    ctx.strokeStyle = lit ? "rgba(212,160,48,0.12)" : "rgba(212,160,48,0.06)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
    ctx.restore();

    // Active/hover border glow (shadow-based bloom)
    if (lit) {
      ctx.save();
      ctx.shadowColor = active ? W.goldBright : W.goldMid;
      ctx.shadowBlur = active ? 12 : 8;
      this.rr(x, y, sz, sz, 6);
      ctx.strokeStyle = active ? W.goldBright : W.goldBright;
      ctx.lineWidth = active ? 1.5 : 1;
      ctx.stroke();
      ctx.restore();
    }

    // Icon — scale 1.05x on hover, centered
    const iconCx = x + sz / 2;
    const iconCy = y + sz / 2 - 2;
    if (lit) {
      ctx.save();
      ctx.translate(iconCx, iconCy);
      const s = active ? 1.08 : 1.05;
      ctx.scale(s, s);
      ctx.translate(-iconCx, -iconCy);
      this.drawActionIcon(icon, iconCx, iconCy, iconSz,
        active ? W.goldGlow : W.goldBright);
      ctx.restore();
    } else {
      this.drawActionIcon(icon, iconCx, iconCy, iconSz, W.textPrime);
    }

    // Label below slot (hide on very small slots)
    if (sz >= 34) {
      ctx.save();
      ctx.font = `${sz < 40 ? 7 : 8}px monospace`;
      ctx.fillStyle = active ? W.goldBright : hovered ? W.textPrime : "#a08040";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(label, x + sz / 2, y + sz + 2);
      ctx.restore();
    }

    // Key hint (top-right) — hide on compact
    if (key && sz >= 38) {
      ctx.save();
      ctx.font = "bold 7px monospace";
      ctx.fillStyle = lit ? W.textDim : W.textMuted;
      ctx.textAlign = "right";
      ctx.textBaseline = "top";
      ctx.fillText(key, x + sz - 3, y + 3);
      ctx.restore();
    }

    // Close press scale save
    if (pressScale !== 1) ctx.restore();
    // Close disabled dimming save
    if (disabled) ctx.restore();
  }

  /** Notification badge — red pill with count, subtle pulse */
  private drawSlotBadge(cx: number, cy: number, count: number, _gameHour: number): void {
    const { ctx } = this;
    const text = count > 99 ? "99+" : String(count);
    const fontSize = count > 99 ? 6 : 7;
    ctx.save();
    ctx.font = `bold ${fontSize}px monospace`;
    const tw = ctx.measureText(text).width;
    const pw = Math.max(12, tw + 6); // pill width
    const ph = 11;

    // Pulse animation — subtle scale breathing
    const pulse = 1 + Math.sin(Date.now() * 0.004) * 0.06;
    ctx.translate(cx, cy);
    ctx.scale(pulse, pulse);

    // Red pill background
    ctx.fillStyle = "#e03030";
    ctx.shadowColor = "#ff2020";
    ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.arc(-pw / 2 + ph / 2, 0, ph / 2, Math.PI * 0.5, Math.PI * 1.5);
    ctx.arc(pw / 2 - ph / 2, 0, ph / 2, -Math.PI * 0.5, Math.PI * 0.5);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    // White count text
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, 0, 0.5);
    ctx.restore();
  }

  /** Amber count badge — subtle item count indicator (bottom-right corner of slot) */
  private drawSlotCountBadge(cx: number, cy: number, count: number): void {
    const { ctx } = this;
    const text = count > 99 ? "99+" : String(count);
    const fontSize = count > 99 ? 6 : 7;
    ctx.save();
    ctx.font = `bold ${fontSize}px monospace`;
    const tw = ctx.measureText(text).width;
    const pw = Math.max(12, tw + 6);
    const ph = 10;

    ctx.translate(cx, cy);

    // Amber pill background (subtle, non-alarming)
    ctx.fillStyle = W.woodMid;
    ctx.beginPath();
    ctx.arc(-pw / 2 + ph / 2, 0, ph / 2, Math.PI * 0.5, Math.PI * 1.5);
    ctx.arc(pw / 2 - ph / 2, 0, ph / 2, -Math.PI * 0.5, Math.PI * 0.5);
    ctx.closePath();
    ctx.fill();
    // Amber border
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 0.8;
    ctx.stroke();

    // Gold count text
    ctx.fillStyle = W.goldBright;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, 0, 0.5);
    ctx.restore();
  }

  /** Draw tooltip popups for hovered UI elements — supports two-line name+description */
  private drawTooltips(SW: number): void {
    const now = Date.now();
    // Check if any tooltip zone is being hovered
    const hovered = this.tooltipZones.find(z =>
      this.mouseX >= z.x && this.mouseX <= z.x + z.w &&
      this.mouseY >= z.y && this.mouseY <= z.y + z.h
    );

    if (hovered) {
      if (this._tooltipLastText !== hovered.text) {
        this._tooltipOpenTime = now;
        this._tooltipLastText = hovered.text;
        this._tooltipPrevText = hovered.text;
      }
    } else {
      if (this._tooltipLastText) {
        this._tooltipExitTime = now;
        this._tooltipLastText = "";
      }
    }

    // Compute alpha: 300ms delay, then 120ms fade-in, 80ms fade-out
    const TOOLTIP_DELAY = 300;
    let tooltipAlpha = 0;
    if (hovered) {
      const elapsed = now - this._tooltipOpenTime;
      tooltipAlpha = elapsed < TOOLTIP_DELAY ? 0 : Math.min(1, (elapsed - TOOLTIP_DELAY) / 120);
    } else if (this._tooltipExitTime) {
      tooltipAlpha = 1 - Math.min(1, (now - this._tooltipExitTime) / 80);
      if (tooltipAlpha <= 0.01) { this._tooltipExitTime = 0; this._tooltipPrevText = ""; return; }
    }
    if (tooltipAlpha <= 0.01) return;

    const tip = hovered ?? this.tooltipZones.find(z => z.text === this._tooltipPrevText) ?? this.tooltipZones[0];
    if (!tip) return;
    const { ctx } = this;
    const rawText = tip.text;

    // Split on newline: first line = title, second line = description
    const nlIdx = rawText.indexOf("\n");
    const title = nlIdx >= 0 ? rawText.slice(0, nlIdx) : rawText;
    const desc  = nlIdx >= 0 ? rawText.slice(nlIdx + 1) : "";
    const hasDesc = desc.length > 0;

    // ── Measure text to compute tooltip dimensions ──
    const TITLE_FONT = "bold 11px monospace";
    const DESC_FONT  = "10px monospace";
    const MAX_TIP_W  = 200;
    const PAD_X = 12;
    const PAD_Y = 8;
    const LINE_GAP = 3;

    ctx.save();

    // Measure title width
    ctx.font = TITLE_FONT;
    const titleW = ctx.measureText(title).width;

    // Word-wrap description within MAX_TIP_W
    let descLines: string[] = [];
    let descMaxW = 0;
    if (hasDesc) {
      ctx.font = DESC_FONT;
      const maxLineW = MAX_TIP_W - PAD_X * 2;
      const words = desc.split(" ");
      let line = "";
      for (const word of words) {
        const test = line ? line + " " + word : word;
        if (ctx.measureText(test).width > maxLineW && line) {
          descLines.push(line);
          descMaxW = Math.max(descMaxW, ctx.measureText(line).width);
          line = word;
        } else {
          line = test;
        }
      }
      if (line) {
        descLines.push(line);
        descMaxW = Math.max(descMaxW, ctx.measureText(line).width);
      }
    }

    const contentW = Math.max(titleW, descMaxW);
    const tw = Math.min(contentW + PAD_X * 2, MAX_TIP_W);

    // Height: title line + optional description lines
    const titleLineH = 14;
    const descLineH  = 13;
    const th = PAD_Y * 2 + titleLineH + (hasDesc ? LINE_GAP + descLines.length * descLineH : 0);

    // Position: centered above the slot, clamped to screen edges
    let tx = tip.x + tip.w / 2 - tw / 2;
    let ty = tip.y - th - 10; // 8px gap + 2px for caret
    let caretBelow = false;
    if (ty < 6) {
      // Flip tooltip below the element if it would go off-screen top
      ty = tip.y + tip.h + 10;
      caretBelow = true;
    }
    tx = Math.max(6, Math.min(tx, SW - tw - 6));

    // Apply fade alpha
    ctx.globalAlpha = tooltipAlpha;

    // Drop shadow (warm-tinted)
    ctx.fillStyle = "rgba(10,6,0,0.45)";
    this.rr(tx + 2, ty + 2, tw, th, 6);
    ctx.fill();

    // Background (gradient for depth)
    this.rr(tx, ty, tw, th, 6);
    const tipGrad = ctx.createLinearGradient(tx, ty, tx, ty + th);
    tipGrad.addColorStop(0, "#2e2016");
    tipGrad.addColorStop(0.5, "#241a10");
    tipGrad.addColorStop(1, "#1a1208");
    ctx.fillStyle = tipGrad;
    ctx.fill();
    // Outer border
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Inner bevel highlight
    this.rr(tx + 1.5, ty + 1.5, tw - 3, th - 3, 5);
    ctx.strokeStyle = "rgba(212,160,48,0.15)";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // ── Caret / pointer triangle ──
    const px = Math.max(tx + 8, Math.min(tip.x + tip.w / 2, tx + tw - 8));
    ctx.fillStyle = "#1a1208";
    ctx.lineJoin = "round";
    if (caretBelow) {
      // Caret points UP from tooltip top edge toward the slot above
      ctx.beginPath();
      ctx.moveTo(px - 6, ty);
      ctx.lineTo(px, ty - 6);
      ctx.lineTo(px + 6, ty);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = W.goldDark;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(px - 6, ty);
      ctx.lineTo(px, ty - 6);
      ctx.lineTo(px + 6, ty);
      ctx.stroke();
      ctx.fillStyle = "#1a1208";
      ctx.fillRect(px - 5, ty - 1, 10, 2);
    } else {
      // Caret points DOWN from tooltip bottom edge toward the slot below
      ctx.beginPath();
      ctx.moveTo(px - 6, ty + th);
      ctx.lineTo(px, ty + th + 6);
      ctx.lineTo(px + 6, ty + th);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = W.goldDark;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(px - 6, ty + th);
      ctx.lineTo(px, ty + th + 6);
      ctx.lineTo(px + 6, ty + th);
      ctx.stroke();
      ctx.fillStyle = "#1a1208";
      ctx.fillRect(px - 5, ty + th - 1, 10, 2);
    }

    // ── Title text (bold, warm cream) ──
    ctx.font = TITLE_FONT;
    ctx.fillStyle = "#f4e4b5";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(title, tx + tw / 2, ty + PAD_Y);

    // ── Description lines (dimmer, smaller) ──
    if (hasDesc) {
      ctx.font = DESC_FONT;
      ctx.fillStyle = W.textDim;
      let dy = ty + PAD_Y + titleLineH + LINE_GAP;
      for (const line of descLines) {
        ctx.fillText(line, tx + tw / 2, dy);
        dy += descLineH;
      }
    }

    ctx.restore();
  }

  /** Procedural icon renderer — each action button gets a recognisable drawn icon */
  private drawActionIcon(type: string, cx: number, cy: number, sz: number, color: string): void {
    const { ctx } = this;
    const s = sz / 2;
    ctx.save();
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    switch (type) {
      case "backpack": {
        // Bag body
        this.rr(cx - s * 0.55, cy - s * 0.1, s * 1.1, s * 0.85, 3);
        ctx.fill();
        // Flap
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.45, cy - s * 0.1);
        ctx.lineTo(cx - s * 0.55, cy - s * 0.5);
        ctx.quadraticCurveTo(cx, cy - s * 0.72, cx + s * 0.55, cy - s * 0.5);
        ctx.lineTo(cx + s * 0.45, cy - s * 0.1);
        ctx.fill();
        // Buckle
        ctx.fillStyle = W.goldBright;
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.28, 2.5, 0, Math.PI * 2);
        ctx.fill();
        // Straps
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.28, cy - s * 0.5);
        ctx.lineTo(cx - s * 0.28, cy - s * 0.78);
        ctx.moveTo(cx + s * 0.28, cy - s * 0.5);
        ctx.lineTo(cx + s * 0.28, cy - s * 0.78);
        ctx.stroke();
        break;
      }
      case "mirror": {
        // Oval glass
        ctx.beginPath();
        ctx.ellipse(cx, cy - s * 0.12, s * 0.42, s * 0.52, 0, 0, Math.PI * 2);
        ctx.fill();
        // Handle
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(cx, cy + s * 0.4);
        ctx.lineTo(cx, cy + s * 0.72);
        ctx.stroke();
        // Shine
        ctx.strokeStyle = W.goldGlow;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.12, cy - s * 0.38);
        ctx.lineTo(cx + s * 0.08, cy - s * 0.08);
        ctx.stroke();
        break;
      }
      case "hammer": {
        // Handle
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(cx + s * 0.42, cy + s * 0.42);
        ctx.lineTo(cx - s * 0.18, cy - s * 0.18);
        ctx.stroke();
        // Head
        ctx.save();
        ctx.translate(cx - s * 0.18, cy - s * 0.18);
        ctx.rotate(-Math.PI / 4);
        ctx.fillStyle = color;
        ctx.fillRect(-s * 0.42, -s * 0.14, s * 0.84, s * 0.28);
        ctx.restore();
        break;
      }
      case "sword": {
        // Blade — angled sword from bottom-right to top-left
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-Math.PI / 4);  // 45° tilt

        // Blade
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.72);     // tip
        ctx.lineTo(-s * 0.1, -s * 0.55);
        ctx.lineTo(-s * 0.07, s * 0.08);
        ctx.lineTo(s * 0.07, s * 0.08);
        ctx.lineTo(s * 0.1, -s * 0.55);
        ctx.closePath();
        ctx.fill();

        // Guard (crossguard)
        ctx.fillStyle = W.goldBright;
        ctx.fillRect(-s * 0.22, s * 0.04, s * 0.44, s * 0.08);

        // Grip
        ctx.fillStyle = color;
        ctx.fillRect(-s * 0.045, s * 0.12, s * 0.09, s * 0.32);

        // Pommel
        ctx.fillStyle = W.goldBright;
        ctx.beginPath();
        ctx.arc(0, s * 0.48, s * 0.07, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
        break;
      }
      case "rotate": {
        // Circular arrow — clockwise rotation indicator
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(cx, cy, s * 0.42, -Math.PI * 0.7, Math.PI * 0.5);
        ctx.stroke();
        // Arrowhead
        const ax = cx + Math.cos(Math.PI * 0.5) * s * 0.42;
        const ay = cy + Math.sin(Math.PI * 0.5) * s * 0.42;
        ctx.beginPath();
        ctx.moveTo(ax + s * 0.18, ay - s * 0.12);
        ctx.lineTo(ax, ay);
        ctx.lineTo(ax - s * 0.12, ay - s * 0.18);
        ctx.stroke();
        break;
      }
      case "move": {
        // Four-way arrows cross
        ctx.lineWidth = 2.5;
        const arm = s * 0.42;
        const head = s * 0.14;
        // Vertical
        ctx.beginPath();
        ctx.moveTo(cx, cy - arm); ctx.lineTo(cx, cy + arm); ctx.stroke();
        // Horizontal
        ctx.beginPath();
        ctx.moveTo(cx - arm, cy); ctx.lineTo(cx + arm, cy); ctx.stroke();
        // Arrow heads (up, down, left, right)
        for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]]) {
          const ex = cx + dx * arm;
          const ey = cy + dy * arm;
          ctx.beginPath();
          ctx.moveTo(ex - dy * head, ey + dx * head);
          ctx.lineTo(ex, ey);
          ctx.lineTo(ex + dy * head, ey - dx * head);
          ctx.stroke();
        }
        break;
      }
      case "hand": {
        // Open hand / pick-up icon
        ctx.lineWidth = 2;
        // Palm
        ctx.beginPath();
        ctx.arc(cx, cy + s * 0.08, s * 0.32, 0, Math.PI * 2);
        ctx.fill();
        // Fingers (3 lines radiating upward)
        ctx.lineWidth = 2.5;
        for (const off of [-s * 0.18, 0, s * 0.18]) {
          ctx.beginPath();
          ctx.moveTo(cx + off, cy - s * 0.12);
          ctx.lineTo(cx + off, cy - s * 0.52);
          ctx.stroke();
        }
        // Thumb (angled)
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.28, cy + s * 0.04);
        ctx.lineTo(cx - s * 0.46, cy - s * 0.2);
        ctx.stroke();
        break;
      }
      case "check": {
        // Checkmark — done/confirm
        ctx.lineWidth = 3;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.38, cy);
        ctx.lineTo(cx - s * 0.08, cy + s * 0.32);
        ctx.lineTo(cx + s * 0.42, cy - s * 0.32);
        ctx.stroke();
        break;
      }
      case "emote": {
        // Smiley face — emote wheel
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx, cy, s * 0.45, 0, Math.PI * 2);
        ctx.stroke();
        // Eyes
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(cx - s * 0.16, cy - s * 0.1, s * 0.06, 0, Math.PI * 2);
        ctx.arc(cx + s * 0.16, cy - s * 0.1, s * 0.06, 0, Math.PI * 2);
        ctx.fill();
        // Smile arc
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy + s * 0.02, s * 0.22, 0.15 * Math.PI, 0.85 * Math.PI);
        ctx.stroke();
        break;
      }
      case "bubble": {
        // Body
        this.rr(cx - s * 0.52, cy - s * 0.52, s * 1.04, s * 0.82, 5);
        ctx.fill();
        // Pointer
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.12, cy + s * 0.3);
        ctx.lineTo(cx - s * 0.32, cy + s * 0.68);
        ctx.lineTo(cx + s * 0.12, cy + s * 0.3);
        ctx.fill();
        // Dots
        ctx.fillStyle = W.slotBg;
        for (const dx of [-s * 0.2, 0, s * 0.2]) {
          ctx.beginPath();
          ctx.arc(cx + dx, cy - s * 0.14, 2, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      case "eye": {
        // Eye outline
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.62, cy);
        ctx.quadraticCurveTo(cx, cy - s * 0.52, cx + s * 0.62, cy);
        ctx.quadraticCurveTo(cx, cy + s * 0.52, cx - s * 0.62, cy);
        ctx.fill();
        // Iris
        ctx.fillStyle = "#3070b0";
        ctx.beginPath();
        ctx.arc(cx, cy, s * 0.22, 0, Math.PI * 2);
        ctx.fill();
        // Pupil
        ctx.fillStyle = "#0a0a14";
        ctx.beginPath();
        ctx.arc(cx, cy, s * 0.1, 0, Math.PI * 2);
        ctx.fill();
        // Highlight
        ctx.fillStyle = "#ffffff";
        ctx.globalAlpha = 0.65;
        ctx.beginPath();
        ctx.arc(cx + s * 0.08, cy - s * 0.08, s * 0.06, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "phone": {
        // Phone body — rounded rectangle
        const pw = s * 0.46;
        const ph = s * 0.72;
        this.rr(cx - pw, cy - ph, pw * 2, ph * 2, 3);
        ctx.fill();
        // Screen — slightly inset, lighter
        ctx.fillStyle = color === W.goldGlow ? "#6080c0" : "#304060";
        ctx.fillRect(cx - pw + 2, cy - ph + 3, pw * 2 - 4, ph * 2 - 7);
        // Home button / bar at bottom
        ctx.fillStyle = W.textDim;
        this.rr(cx - pw * 0.35, cy + ph - 3, pw * 0.7, 1.5, 0.5);
        ctx.fill();
        // Screen glint
        ctx.strokeStyle = "#ffffff";
        ctx.globalAlpha = 0.25;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(cx - pw + 3, cy - ph + 5);
        ctx.lineTo(cx - pw + 3, cy - ph + 12);
        ctx.stroke();
        break;
      }
      case "gear": {
        const teeth = 8;
        const outerR = s * 0.62;
        const innerR = s * 0.42;
        ctx.beginPath();
        for (let i = 0; i < teeth; i++) {
          const a0 = (i / teeth) * Math.PI * 2 - Math.PI / teeth / 2;
          const a1 = a0 + Math.PI / teeth * 0.55;
          const a2 = a0 + Math.PI / teeth;
          const a3 = a0 + Math.PI / teeth * 1.55;
          const a4 = a0 + Math.PI * 2 / teeth;
          if (i === 0) ctx.moveTo(cx + Math.cos(a0) * innerR, cy + Math.sin(a0) * innerR);
          ctx.lineTo(cx + Math.cos(a1) * outerR, cy + Math.sin(a1) * outerR);
          ctx.lineTo(cx + Math.cos(a2) * outerR, cy + Math.sin(a2) * outerR);
          ctx.lineTo(cx + Math.cos(a3) * innerR, cy + Math.sin(a3) * innerR);
          ctx.lineTo(cx + Math.cos(a4) * innerR, cy + Math.sin(a4) * innerR);
        }
        ctx.closePath();
        ctx.fill();
        // Centre hole
        ctx.fillStyle = W.slotBg;
        ctx.beginPath();
        ctx.arc(cx, cy, s * 0.18, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
    }
    ctx.restore();
  }

  /** Gold ornamental divider line with diamond end-caps */
  private drawGoldDivider(x: number, y1: number, y2: number): void {
    const { ctx } = this;
    ctx.save();
    const grad = ctx.createLinearGradient(x, y1, x, y2);
    grad.addColorStop(0, "transparent");
    grad.addColorStop(0.15, W.goldMid);
    grad.addColorStop(0.5, W.goldBright);
    grad.addColorStop(0.85, W.goldMid);
    grad.addColorStop(1, "transparent");
    ctx.strokeStyle = grad;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y1);
    ctx.lineTo(x, y2);
    ctx.stroke();
    // Diamond centre
    const my = (y1 + y2) / 2;
    ctx.fillStyle = W.goldBright;
    ctx.beginPath();
    ctx.moveTo(x, my - 4); ctx.lineTo(x + 3, my);
    ctx.lineTo(x, my + 4); ctx.lineTo(x - 3, my);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /** Decorative corner bracket (small gold L-shape) */
  private drawCornerBracket(x: number, y: number, flip: boolean): void {
    const { ctx } = this;
    ctx.save();
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    const d = flip ? -1 : 1;
    ctx.beginPath();
    ctx.moveTo(x + d * 12, y);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + 12);
    ctx.stroke();
    // Small end dot
    ctx.fillStyle = W.goldMid;
    ctx.beginPath();
    ctx.arc(x + d * 12, y, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y + 12, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** Player crest — decorative shield with initial */
  private drawCrest(x: number, y: number, w: number, h: number, state: GameState): void {
    const { ctx } = this;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x + w, y + h * 0.15);
    ctx.lineTo(x + w, y + h * 0.55);
    ctx.quadraticCurveTo(x + w, y + h * 0.85, x + w / 2, y + h);
    ctx.quadraticCurveTo(x, y + h * 0.85, x, y + h * 0.55);
    ctx.lineTo(x, y + h * 0.15);
    ctx.closePath();
    // Fill + inner gradient
    const sg = ctx.createLinearGradient(x, y, x, y + h);
    sg.addColorStop(0, W.woodLight);
    sg.addColorStop(1, W.woodDark);
    ctx.fillStyle = sg;
    ctx.fill();
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Inner bevel
    ctx.strokeStyle = "rgba(212,160,48,0.2)";
    ctx.lineWidth = 1;
    ctx.stroke();
    // Initial
    const initial = (state.player.name ?? "?").charAt(0).toUpperCase();
    ctx.font = "bold 16px monospace";
    ctx.fillStyle = W.goldGlow;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(initial, x + w / 2, y + h * 0.42);
    ctx.restore();
  }

  /** Five-pointed star for reputation */
  private drawStar5(cx: number, cy: number, r: number, color: string): void {
    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = (i * Math.PI * 2) / 5 - Math.PI / 2;
      const b = a + Math.PI / 5;
      ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      ctx.lineTo(cx + Math.cos(b) * r * 0.45, cy + Math.sin(b) * r * 0.45);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /** Time display section (right side of action bar) */
  private drawTimeSection(x: number, y: number, w: number, h: number, state: GameState): void {
    const { ctx } = this;
    const my = y + h / 2;

    // Clock icon
    const clockX = x + 18;
    ctx.save();
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(clockX, my, 10, 0, Math.PI * 2);
    ctx.stroke();
    // Tick marks
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
      const r0 = i % 3 === 0 ? 7 : 8;
      ctx.beginPath();
      ctx.moveTo(clockX + Math.cos(a) * r0, my + Math.sin(a) * r0);
      ctx.lineTo(clockX + Math.cos(a) * 9.5, my + Math.sin(a) * 9.5);
      ctx.stroke();
    }
    // Hour hand
    const gh = state.time.gameHour;
    const hourAng = ((gh % 12) / 12) * Math.PI * 2 - Math.PI / 2;
    ctx.lineWidth = 2;
    ctx.strokeStyle = W.goldBright;
    ctx.beginPath();
    ctx.moveTo(clockX, my);
    ctx.lineTo(clockX + Math.cos(hourAng) * 5, my + Math.sin(hourAng) * 5);
    ctx.stroke();
    // Minute hand
    const minFrac = (gh % 1);
    const minAng = minFrac * Math.PI * 2 - Math.PI / 2;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(clockX, my);
    ctx.lineTo(clockX + Math.cos(minAng) * 8, my + Math.sin(minAng) * 8);
    ctx.stroke();
    ctx.restore();

    // Time text
    const hr = Math.floor(gh) % 12 || 12;
    const ampm = Math.floor(gh) < 12 ? "am" : "pm";
    const min = String(Math.floor(minFrac * 60)).padStart(2, "0");
    ctx.save();
    ctx.font = "bold 12px monospace";
    ctx.fillStyle = W.textPrime;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(`${hr}:${min}`, x + 34, my - 7);
    ctx.font = "9px monospace";
    ctx.fillStyle = W.textDim;
    ctx.fillText(ampm, x + 34 + ctx.measureText(`${hr}:${min}`).width + 3, my - 7);
    ctx.restore();

    // Sun / moon indicator
    const isDark = gh < 6 || gh >= 20;
    ctx.save();
    ctx.fillStyle = isDark ? "#6080c0" : W.goldBright;
    ctx.beginPath();
    ctx.arc(x + w - 18, my - 4, 6, 0, Math.PI * 2);
    ctx.fill();
    if (isDark) {
      ctx.fillStyle = W.slotBg;
      ctx.beginPath();
      ctx.arc(x + w - 15, my - 6, 5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Season label
    ctx.save();
    ctx.font = "8px monospace";
    ctx.fillStyle = W.textMuted;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(state.time.season ?? "", x + 34, my + 8);
    ctx.restore();
  }

  /** Compact time — just clock face and time text, no season or sun/moon */
  private drawCompactTime(x: number, y: number, w: number, h: number, state: GameState): void {
    const { ctx } = this;
    const my = y + h / 2;
    const gh = state.time.gameHour;

    // Small clock face
    const clockX = x + 12;
    ctx.save();
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(clockX, my, 8, 0, Math.PI * 2);
    ctx.stroke();
    // Hour hand
    const hourAng = ((gh % 12) / 12) * Math.PI * 2 - Math.PI / 2;
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = W.goldBright;
    ctx.beginPath();
    ctx.moveTo(clockX, my);
    ctx.lineTo(clockX + Math.cos(hourAng) * 4, my + Math.sin(hourAng) * 4);
    ctx.stroke();
    // Minute hand
    const minFrac = gh % 1;
    const minAng = minFrac * Math.PI * 2 - Math.PI / 2;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(clockX, my);
    ctx.lineTo(clockX + Math.cos(minAng) * 6, my + Math.sin(minAng) * 6);
    ctx.stroke();
    ctx.restore();

    // Time text
    const hr = Math.floor(gh) % 12 || 12;
    const ampm = Math.floor(gh) < 12 ? "a" : "p";
    const min = String(Math.floor(minFrac * 60)).padStart(2, "0");
    ctx.save();
    ctx.font = "bold 10px monospace";
    ctx.fillStyle = W.textPrime;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(`${hr}:${min}${ampm}`, x + 24, my);
    ctx.restore();
  }

  // ─── Compass ────────────────────────────────────────────────────────────────

  private drawCompass(state: GameState, _SW: number): void {
    const { ctx } = this;
    const cx = 16 + COMPASS_R;
    const cy = 16 + COMPASS_R;

    // Outer ring — wood bezel
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, COMPASS_R, 0, Math.PI * 2);
    const rg = ctx.createRadialGradient(cx, cy, COMPASS_R * 0.6, cx, cy, COMPASS_R);
    rg.addColorStop(0, W.woodMid);
    rg.addColorStop(1, W.woodDark);
    ctx.fillStyle = rg;
    ctx.fill();
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 2;
    ctx.stroke();
    // Inner bevel
    ctx.beginPath();
    ctx.arc(cx, cy, COMPASS_R - 2, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(212,160,48,0.3)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // Tick marks (every 45°)
    ctx.save();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
      const isMajor = i % 2 === 0;
      const r0 = COMPASS_R - (isMajor ? 14 : 10);
      const r1 = COMPASS_R - 5;
      ctx.strokeStyle = isMajor ? W.goldMid : W.textMuted;
      ctx.lineWidth = isMajor ? 1.5 : 0.8;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
      ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      ctx.stroke();
    }
    ctx.restore();

    // Cardinal labels (N E S W)
    const labels: [string, number][] = [["N", -Math.PI / 2], ["E", 0], ["S", Math.PI / 2], ["W", Math.PI]];
    ctx.save();
    ctx.font = "bold 9px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const [lbl, a] of labels) {
      const lr = COMPASS_R - 20;
      ctx.fillStyle = lbl === "N" ? W.goldGlow : W.textDim;
      ctx.fillText(lbl, cx + Math.cos(a) * lr, cy + Math.sin(a) * lr);
    }
    ctx.restore();

    // Needle — points in player's facing direction
    const dir = state.player.direction as string;
    const angle = DIR_ANGLE[dir] ?? DIR_ANGLE["S"];
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    // Bright tip (forward)
    ctx.fillStyle = W.goldBright;
    ctx.beginPath();
    ctx.moveTo(COMPASS_R * 0.55, 0);
    ctx.lineTo(-2, -4);
    ctx.lineTo(-2, 4);
    ctx.closePath();
    ctx.fill();
    // Dim tail (backward)
    ctx.fillStyle = W.textMuted;
    ctx.beginPath();
    ctx.moveTo(-COMPASS_R * 0.35, 0);
    ctx.lineTo(2, -3);
    ctx.lineTo(2, 3);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Centre dot
    ctx.save();
    ctx.fillStyle = W.goldBright;
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = W.goldDark;
    ctx.beginPath();
    ctx.arc(cx, cy, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Compass tooltip on hover
    const compassHovered =
      (this.mouseX - cx) ** 2 + (this.mouseY - cy) ** 2 <= COMPASS_R * COMPASS_R;
    if (compassHovered) {
      const DIR_NAMES: Record<string, string> = {
        N: "North", NE: "Northeast", E: "East", SE: "Southeast",
        S: "South", SW: "Southwest", W: "West", NW: "Northwest",
      };
      const dir = state.player.direction as string;
      const dirName = DIR_NAMES[dir] ?? dir;
      this.tooltipZones.push({
        x: cx - COMPASS_R, y: cy - COMPASS_R,
        w: COMPASS_R * 2, h: COMPASS_R * 2,
        text: `Compass\nFacing ${dirName}`,
      });
    }
  }

  // ─── Quest Tracker ──────────────────────────────────────────────────────────

  /** Compact quest tracker below the minimap — shows active quest name, current step, progress bar */
  private drawQuestTracker(state: GameState, SW: number): void {
    // Find the first active quest
    const activeQP = state.player.quests.find(q => q.status === "active");
    if (!activeQP) return;
    const questDef = QUESTS.find(q => q.id === activeQP.questId);
    if (!questDef) return;

    const { ctx } = this;
    const trackerW = 160;
    const trackerX = SW - 16 - trackerW; // right-aligned with minimap right edge
    const trackerY = 16 + MINIMAP_R * 2 + 18; // below minimap circle + room name

    // Find current step (first incomplete step)
    const currentStep = questDef.steps.find(s => !activeQP.completedSteps.includes(s.id));
    const totalSteps = questDef.steps.length;
    const doneSteps = activeQP.completedSteps.length;

    // Panel background — warm dark wood with subtle border
    ctx.save();
    this.rr(trackerX, trackerY, trackerW, 54, 6);
    ctx.fillStyle = "rgba(18, 14, 8, 0.88)";
    ctx.fill();
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Arc color indicator (left edge accent)
    const arcColors: Record<string, string> = {
      social: "#60b0e0", creative: "#e0a040", exploration: "#60c070",
      stewardship: "#a0d060", consciousness: "#c080e0",
    };
    const arcCol = arcColors[questDef.arc] ?? W.goldMid;
    ctx.fillStyle = arcCol;
    ctx.fillRect(trackerX + 2, trackerY + 6, 3, 42);

    // Quest name
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = W.goldBright;
    ctx.textBaseline = "top";
    ctx.textAlign = "left";
    ctx.fillText(questDef.name.slice(0, 20), trackerX + 10, trackerY + 5);

    // Current step description (truncated)
    if (currentStep) {
      ctx.font = "8px monospace";
      ctx.fillStyle = W.textDim;
      const stepText = currentStep.description.length > 26
        ? currentStep.description.slice(0, 24) + "…"
        : currentStep.description;
      ctx.fillText(stepText, trackerX + 10, trackerY + 18);
    }

    // Progress bar
    const barX = trackerX + 10;
    const barY2 = trackerY + 32;
    const barW = trackerW - 20;
    const barH2 = 6;
    // Background track
    this.rr(barX, barY2, barW, barH2, 3);
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fill();
    // Filled portion
    const fillW = Math.max(2, barW * activeQP.progress);
    this.rr(barX, barY2, fillW, barH2, 3);
    ctx.fillStyle = arcCol;
    ctx.fill();

    // Step count
    ctx.font = "bold 7px monospace";
    ctx.fillStyle = W.textMuted;
    ctx.textAlign = "right";
    ctx.fillText(`${doneSteps}/${totalSteps}`, trackerX + trackerW - 8, trackerY + 42);

    ctx.restore();
  }

  // ─── Minimap ────────────────────────────────────────────────────────────────

  private drawMinimap(state: GameState, SW: number): void {
    const { ctx } = this;
    const room = state.currentRoom;
    if (!room) return;
    const now = Date.now();

    const cx = SW - 16 - MINIMAP_R;
    const cy = 16 + MINIMAP_R;

    // ── Outer bezel — layered wood frame with depth ──────────────────────
    ctx.save();
    // Drop shadow
    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur = 12;
    ctx.shadowOffsetY = 3;
    ctx.beginPath();
    ctx.arc(cx, cy, MINIMAP_R + 3, 0, Math.PI * 2);
    ctx.fillStyle = "#0a0804";
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Outer gold ring
    ctx.beginPath();
    ctx.arc(cx, cy, MINIMAP_R + 2, 0, Math.PI * 2);
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 3;
    ctx.stroke();

    // Main bezel — radial gradient for 3D curvature
    ctx.beginPath();
    ctx.arc(cx, cy, MINIMAP_R, 0, Math.PI * 2);
    const bezel = ctx.createRadialGradient(cx - 15, cy - 15, 0, cx, cy, MINIMAP_R);
    bezel.addColorStop(0, "#3a2a14");
    bezel.addColorStop(0.7, "#2a1d0e");
    bezel.addColorStop(1, "#1a1208");
    ctx.fillStyle = bezel;
    ctx.fill();
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Inner bevelled edge
    ctx.beginPath();
    ctx.arc(cx, cy, MINIMAP_R - 3, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(212,160,48,0.15)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Map background fill
    ctx.beginPath();
    ctx.arc(cx, cy, MINIMAP_R - 4, 0, Math.PI * 2);
    const mapBg = ctx.createRadialGradient(cx, cy, 0, cx, cy, MINIMAP_R - 4);
    mapBg.addColorStop(0, "#161008");
    mapBg.addColorStop(1, "#0c0804");
    ctx.fillStyle = mapBg;
    ctx.fill();
    ctx.restore();

    // ── Clip for tile content ────────────────────────────────────────────
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, MINIMAP_R - 4, 0, Math.PI * 2);
    ctx.clip();

    // Compute tile pixel size
    const mapW = room.width;
    const mapH = room.height;
    const maxDim = Math.max(mapW, mapH);
    const tSz = Math.floor((MINIMAP_R * 2 - 16) / maxDim);
    const oX = cx - (mapW * tSz) / 2;
    const oY = cy - (mapH * tSz) / 2;

    // ── Tiles — elevation-aware with subtle depth ────────────────────────
    for (let ty = 0; ty < mapH; ty++) {
      for (let tx = 0; tx < mapW; tx++) {
        const tile = room.tiles[ty]?.[tx];
        if (!tile) continue;
        const px = oX + tx * tSz;
        const py = oY + ty * tSz;
        const baseColor = MINI_TILE[tile.type] ?? "#3a3a3a";
        ctx.fillStyle = baseColor;
        ctx.fillRect(px, py, tSz - 0.5, tSz - 0.5);

        // Elevation highlight — brighter for higher tiles
        if (tile.elevation > 0) {
          ctx.fillStyle = `rgba(255,240,200,${Math.min(0.15, tile.elevation * 0.06)})`;
          ctx.fillRect(px, py, tSz - 0.5, tSz - 0.5);
        }

        // Non-walkable = darker overlay (walls, obstacles)
        if (!tile.walkable) {
          ctx.fillStyle = "rgba(0,0,0,0.25)";
          ctx.fillRect(px, py, tSz - 0.5, tSz - 0.5);
        }
      }
    }

    // ── Furniture — small amber rectangles with slight glow ──────────────
    for (const f of room.furniture) {
      const fx = oX + (f.tileX + 0.5) * tSz;
      const fy = oY + (f.tileY + 0.5) * tSz;
      const fSz = Math.max(1.5, tSz * 0.28);
      // Glow
      ctx.save();
      ctx.shadowColor = "rgba(184,149,106,0.3)";
      ctx.shadowBlur = 2;
      ctx.fillStyle = "#b8956a";
      ctx.fillRect(fx - fSz, fy - fSz, fSz * 2, fSz * 2);
      ctx.restore();
    }

    // ── Subtle grid overlay — very faint cardinal lines ──────────────────
    ctx.save();
    ctx.globalAlpha = 0.04;
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(cx, cy - MINIMAP_R * 0.7);
    ctx.lineTo(cx, cy + MINIMAP_R * 0.7);
    ctx.moveTo(cx - MINIMAP_R * 0.7, cy);
    ctx.lineTo(cx + MINIMAP_R * 0.7, cy);
    ctx.stroke();
    ctx.restore();

    // ── Creature markers ─────────────────────────────────────────────────
    const dotR = Math.max(2, tSz * 0.35);
    for (const c of state.creatures) {
      const crx = oX + (c.position.tileX + 0.5) * tSz;
      const cry = oY + (c.position.tileY + 0.5) * tSz;
      if (c.ownerId === null) {
        // Hostile — multi-zone threat detection cone
        const cDir = c.direction as string;
        const cAngle = DIR_ANGLE[cDir] ?? DIR_ANGLE["S"];

        // Outer peripheral threat — wide, dim
        ctx.save();
        ctx.translate(crx, cry);
        ctx.rotate(cAngle);
        const outerLen = tSz * 3;
        const outerSpread = tSz * 2;
        const outerG = ctx.createLinearGradient(0, 0, outerLen, 0);
        outerG.addColorStop(0, "rgba(208,64,64,0.05)");
        outerG.addColorStop(1, "rgba(208,64,64,0)");
        ctx.fillStyle = outerG;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(outerLen, -outerSpread);
        ctx.lineTo(outerLen, outerSpread);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Inner aggro zone — tight, pulsing
        const aggroPulse = 0.08 + Math.sin(now * 0.004 + c.position.tileX * 3) * 0.04;
        ctx.save();
        ctx.translate(crx, cry);
        ctx.rotate(cAngle);
        const innerLen = tSz * 2;
        const innerSpread = tSz * 0.8;
        const innerG = ctx.createLinearGradient(0, 0, innerLen, 0);
        innerG.addColorStop(0, `rgba(240,80,48,${aggroPulse})`);
        innerG.addColorStop(1, "rgba(240,80,48,0)");
        ctx.fillStyle = innerG;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(innerLen, -innerSpread);
        ctx.lineTo(innerLen, innerSpread);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Red dot with pulse + tiny glow
        const hostilePulse = 0.6 + Math.sin(now * 0.005 + c.position.tileX * 7) * 0.4;
        ctx.save();
        ctx.shadowColor = "rgba(208,64,64,0.4)";
        ctx.shadowBlur = 3;
        ctx.globalAlpha = hostilePulse;
        ctx.fillStyle = "#d04040";
        ctx.beginPath();
        ctx.arc(crx, cry, dotR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        // Target ring on current combat target
        if (c.id === state.player.target.id) {
          ctx.save();
          ctx.strokeStyle = "#ff6040";
          ctx.lineWidth = 1;
          ctx.globalAlpha = 0.6 + Math.sin(now * 0.008) * 0.3;
          ctx.beginPath();
          ctx.arc(crx, cry, dotR + 2, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
      } else {
        ctx.fillStyle = "#50b060";
        ctx.beginPath();
        ctx.arc(crx, cry, dotR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── NPC markers with quest icons ─────────────────────────────────────
    for (const npc of state.npcs) {
      const nx = oX + (npc.position.tileX + 0.5) * tSz;
      const ny = oY + (npc.position.tileY + 0.5) * tSz;
      const nr = Math.max(2.5, tSz * 0.4);

      // Base dot — green circle for friendly NPC
      ctx.save();
      ctx.shadowColor = "rgba(96,200,72,0.3)";
      ctx.shadowBlur = 2;
      ctx.fillStyle = "#60c848";
      ctx.beginPath();
      ctx.arc(nx, ny, nr * 0.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Quest marker above NPC dot
      const mark = npc.questMark;
      if (mark) {
        const markY = ny - nr - 3;
        const isGold = mark === "available" || mark === "ready_complete";
        const markColor = isGold ? "#f0c860" : "#c0c0c0";
        const markGlyph = mark === "available" ? "!" : "?";
        const markPulse = isGold ? 0.8 + Math.sin(now * 0.006) * 0.2 : 0.7;

        ctx.save();
        ctx.globalAlpha = markPulse;
        // Tiny glow behind the mark
        if (isGold) {
          ctx.shadowColor = "rgba(240,200,96,0.6)";
          ctx.shadowBlur = 4;
        }
        ctx.font = `bold ${Math.max(7, Math.round(nr * 1.2))}px sans-serif`;
        ctx.fillStyle = markColor;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(markGlyph, nx, markY);
        ctx.restore();
      }
    }

    // ── NPC attention / multi-zone vision cones ──────────────────────────
    for (const npc of state.npcs) {
      const nvx = oX + (npc.position.tileX + 0.5) * tSz;
      const nvy = oY + (npc.position.tileY + 0.5) * tSz;
      const npcFocus = npc.gazeFocus ?? 0;
      const npcGA = npc.gazeAngle ?? 0;

      // Layer 1: Peripheral awareness — wide, faint, always present
      const periLen = tSz * 3.5;
      const periSpread = tSz * 2.2;
      ctx.save();
      ctx.translate(nvx, nvy);
      ctx.rotate(npcGA);
      const periGrad = ctx.createLinearGradient(0, 0, periLen, 0);
      periGrad.addColorStop(0, "rgba(96,200,72,0.04)");
      periGrad.addColorStop(1, "rgba(96,200,72,0)");
      ctx.fillStyle = periGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(periLen, -periSpread);
      ctx.lineTo(periLen, periSpread);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Layer 2: Active focus — tighter, brighter, narrows with engagement
      const focusLen = tSz * (2.5 + npcFocus * 1.5);
      const focusSpread = tSz * (1.0 - npcFocus * 0.55);
      const focusAlpha = 0.06 + npcFocus * 0.16;
      ctx.save();
      ctx.translate(nvx, nvy);
      ctx.rotate(npcGA);
      const focusGrad = ctx.createLinearGradient(0, 0, focusLen, 0);
      focusGrad.addColorStop(0, `rgba(96,200,72,${focusAlpha})`);
      focusGrad.addColorStop(0.7, `rgba(96,200,72,${focusAlpha * 0.3})`);
      focusGrad.addColorStop(1, "rgba(96,200,72,0)");
      ctx.fillStyle = focusGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(focusLen, -focusSpread);
      ctx.lineTo(focusLen, focusSpread);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // ── Player — gold diamond with facing + attention cones ──────────────
    const ppx = oX + (state.player.position.tileX + 0.5) * tSz;
    const ppy = oY + (state.player.position.tileY + 0.5) * tSz;

    // 1. Body facing cone — wide, warm gold (shows direction character faces)
    const pDir = state.player.direction as string;
    const pAngle = DIR_ANGLE[pDir] ?? DIR_ANGLE["S"];
    ctx.save();
    ctx.translate(ppx, ppy);
    ctx.rotate(pAngle);
    const coneLen = tSz * 3.5;
    const coneSpread = tSz * 1.8;
    const coneGrad = ctx.createLinearGradient(0, 0, coneLen, 0);
    coneGrad.addColorStop(0, "rgba(240,200,96,0.25)");
    coneGrad.addColorStop(1, "rgba(240,200,96,0)");
    ctx.fillStyle = coneGrad;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(coneLen, -coneSpread);
    ctx.lineTo(coneLen, coneSpread);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // 2. Eye/attention cone — narrower, brighter, follows gaze
    //    Spread narrows with focus: idle=wide peripheral, locked=tight beam
    const gazeAngle = state.player.gazeAngle ?? pAngle;
    const gazeFocus = state.player.gazeFocus ?? 0;
    const gazeConeLen = tSz * (3 + gazeFocus * 1.5);
    const gazeConeSpread = tSz * (1.2 - gazeFocus * 0.7);
    const gazeAlpha = 0.15 + gazeFocus * 0.2;
    ctx.save();
    ctx.translate(ppx, ppy);
    ctx.rotate(gazeAngle);
    const gazeGrad = ctx.createLinearGradient(0, 0, gazeConeLen, 0);
    gazeGrad.addColorStop(0, `rgba(180,240,255,${gazeAlpha})`);
    gazeGrad.addColorStop(0.6, `rgba(180,240,255,${gazeAlpha * 0.4})`);
    gazeGrad.addColorStop(1, "rgba(180,240,255,0)");
    ctx.fillStyle = gazeGrad;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(gazeConeLen, -gazeConeSpread);
    ctx.lineTo(gazeConeLen, gazeConeSpread);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Player diamond with pulsing glow
    const pd = Math.max(3, tSz * 0.5);
    const playerPulse = 0.85 + Math.sin(now * 0.004) * 0.15;
    ctx.save();
    ctx.shadowColor = `rgba(240,200,96,${playerPulse * 0.5})`;
    ctx.shadowBlur = 6;
    ctx.fillStyle = W.goldGlow;
    ctx.beginPath();
    ctx.moveTo(ppx, ppy - pd);
    ctx.lineTo(ppx + pd, ppy);
    ctx.lineTo(ppx, ppy + pd);
    ctx.lineTo(ppx - pd, ppy);
    ctx.closePath();
    ctx.fill();
    // Bright center pip
    ctx.fillStyle = "#fff8e0";
    ctx.beginPath();
    ctx.arc(ppx, ppy, pd * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // ── Edge vignette — subtle inner shadow for depth ────────────────────
    const vignette = ctx.createRadialGradient(cx, cy, MINIMAP_R * 0.6, cx, cy, MINIMAP_R - 4);
    vignette.addColorStop(0, "rgba(0,0,0,0)");
    vignette.addColorStop(1, "rgba(0,0,0,0.35)");
    ctx.fillStyle = vignette;
    ctx.beginPath();
    ctx.arc(cx, cy, MINIMAP_R - 4, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore(); // un-clip

    // ── Combat danger pulse — animated red glow ring ─────────────────────
    if (state.player.combatState.active) {
      ctx.save();
      const dangerPulse = 0.15 + Math.sin(now * 0.004) * 0.1;
      ctx.beginPath();
      ctx.arc(cx, cy, MINIMAP_R - 1, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(200, 50, 40, ${dangerPulse})`;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, MINIMAP_R - 3, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(200, 50, 40, ${dangerPulse * 0.5})`;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    }

    // ── Compass cardinal markers on bezel ────────────────────────────────
    ctx.save();
    ctx.font = "bold 7px monospace";
    ctx.fillStyle = W.goldBright;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.globalAlpha = 0.6;
    ctx.fillText("N", cx, cy - MINIMAP_R + 7);
    ctx.globalAlpha = 0.3;
    ctx.fillText("S", cx, cy + MINIMAP_R - 7);
    ctx.fillText("W", cx - MINIMAP_R + 8, cy);
    ctx.fillText("E", cx + MINIMAP_R - 7, cy);
    ctx.restore();

    // ── Room name below minimap ──────────────────────────────────────────
    ctx.save();
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = W.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(room.name ?? "", cx, cy + MINIMAP_R + 5);
    ctx.restore();

    // ── Visitor count badge — top-right of minimap ─────────────────────
    const visitorCount = (state.otherPlayers?.length ?? 0) + 1; // +1 for self
    if (visitorCount > 0) {
      const badgeX = cx + MINIMAP_R * 0.6;
      const badgeY = cy - MINIMAP_R * 0.7;
      const badgeText = visitorCount > 99 ? "99+" : String(visitorCount);
      ctx.save();
      ctx.font = "bold 7px monospace";
      const tw = ctx.measureText(badgeText).width;
      const pw = Math.max(14, tw + 6);
      // Pill background
      ctx.fillStyle = "#3a6a3a";
      ctx.beginPath();
      ctx.arc(badgeX - pw / 2 + 5.5, badgeY, 5.5, Math.PI * 0.5, Math.PI * 1.5);
      ctx.arc(badgeX + pw / 2 - 5.5, badgeY, 5.5, -Math.PI * 0.5, Math.PI * 0.5);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "#60b060";
      ctx.lineWidth = 0.8;
      ctx.stroke();
      // Count text
      ctx.fillStyle = "#d0f0d0";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(badgeText, badgeX, badgeY + 0.5);
      ctx.restore();
    }

    // ── Hover tooltip ────────────────────────────────────────────────────
    const mapHovered =
      (this.mouseX - cx) ** 2 + (this.mouseY - cy) ** 2 <= MINIMAP_R * MINIMAP_R;
    if (mapHovered) {
      // Check if hovering an NPC on the minimap
      let hoverText = `${room.name ?? "Room"}\nClick tile to move`;
      for (const npc of state.npcs) {
        const nx = oX + (npc.position.tileX + 0.5) * tSz;
        const ny = oY + (npc.position.tileY + 0.5) * tSz;
        if (Math.abs(this.mouseX - nx) < tSz && Math.abs(this.mouseY - ny) < tSz) {
          hoverText = `${npc.name}\n${npc.title}`;
          break;
        }
      }
      this.tooltipZones.push({
        x: cx - MINIMAP_R, y: cy - MINIMAP_R,
        w: MINIMAP_R * 2, h: MINIMAP_R * 2,
        text: hoverText,
      });
    }

    // ── Expand button — opens full map overlay ─────────────────────────
    const btnSz = 14;
    const btnX = cx + MINIMAP_R * 0.55;
    const btnY = cy + MINIMAP_R * 0.55;
    // Draw expand icon (two diagonal arrows)
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = W.woodMid;
    ctx.beginPath();
    ctx.arc(btnX, btnY, btnSz / 2 + 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 0.5;
    ctx.stroke();
    ctx.globalAlpha = 0.7;
    ctx.font = "bold 9px sans-serif";
    ctx.fillStyle = W.goldBright;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("⤢", btnX, btnY);
    ctx.restore();

    this.addHit(btnX - btnSz / 2 - 2, btnY - btnSz / 2 - 2, btnSz + 4, btnSz + 4, (s) => {
      s.ui.mapOpen = true;
      s.ui.mapPanX = 0;
      s.ui.mapPanY = 0;
      s.ui.uiSounds.push("ui_open");
    });

    // ── Click-to-navigate ────────────────────────────────────────────────
    const mmOX = oX, mmOY = oY, mmTSz = tSz;
    const mmHalf = Math.floor(MINIMAP_R * 0.707);
    this.addHit(cx - mmHalf, cy - mmHalf, mmHalf * 2, mmHalf * 2, (s) => {
      const tileX = Math.floor((this.mouseX - mmOX) / mmTSz);
      const tileY = Math.floor((this.mouseY - mmOY) / mmTSz);
      const rm = s.currentRoom;
      if (!rm) return;
      if (tileX < 0 || tileX >= rm.width || tileY < 0 || tileY >= rm.height) return;
      const tile = rm.tiles[tileY]?.[tileX];
      if (!tile || !tile.walkable) return;
      s.ui.clickIndicator = { tileX, tileY, time: performance.now(), reachable: true };
      s.player.targetPosition = { tileX, tileY };
      s.player.movementQueue = [{ tileX, tileY }];
      s.ui.uiSounds.push("ui_click");
    });
  }

  // ─── Full-Screen Room Map ──────────────────────────────────────────────────

  /** Track drag state for map panning */
  private _mapDragging = false;
  private _mapDragStartX = 0;
  private _mapDragStartY = 0;
  private _mapDragPanStartX = 0;
  private _mapDragPanStartY = 0;

  private drawFullMap(state: GameState, SW: number, SH: number): void {
    const { ctx } = this;
    const room = state.currentRoom;
    if (!room) return;
    const now = Date.now();
    const ui = state.ui;

    // ── Backdrop — dark overlay with warm vignette ───────────────────────
    ctx.save();
    ctx.fillStyle = "rgba(8, 6, 4, 0.88)";
    ctx.fillRect(0, 0, SW, SH);
    // Vignette
    const vig = ctx.createRadialGradient(SW / 2, SH / 2, Math.min(SW, SH) * 0.3, SW / 2, SH / 2, Math.max(SW, SH) * 0.6);
    vig.addColorStop(0, "rgba(0,0,0,0)");
    vig.addColorStop(1, "rgba(0,0,0,0.3)");
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, SW, SH);
    ctx.restore();

    // ── Map panel — centered with ornate border ─────────────────────────
    const margin = 40;
    const panelW = SW - margin * 2;
    const panelH = SH - margin * 2;
    const panelX = margin;
    const panelY = margin;

    // Ornate frame
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 4;
    roundRect(ctx, panelX - 3, panelY - 3, panelW + 6, panelH + 6, 12);
    ctx.fillStyle = "#0e0a06";
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Outer gold border
    roundRect(ctx, panelX - 2, panelY - 2, panelW + 4, panelH + 4, 11);
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Panel fill — parchment-like warm dark
    roundRect(ctx, panelX, panelY, panelW, panelH, 10);
    const panelBg = ctx.createLinearGradient(panelX, panelY, panelX, panelY + panelH);
    panelBg.addColorStop(0, "#1a1408");
    panelBg.addColorStop(0.5, "#141008");
    panelBg.addColorStop(1, "#0e0a06");
    ctx.fillStyle = panelBg;
    ctx.fill();
    ctx.strokeStyle = "rgba(212,160,48,0.2)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Inner bevel
    roundRect(ctx, panelX + 2, panelY + 2, panelW - 4, panelH - 4, 8);
    ctx.strokeStyle = "rgba(212,160,48,0.08)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
    ctx.restore();

    // ── Clip map content ─────────────────────────────────────────────────
    ctx.save();
    roundRect(ctx, panelX + 4, panelY + 4, panelW - 8, panelH - 8, 7);
    ctx.clip();

    // Compute tile size to fit room with padding
    const mapW = room.width;
    const mapH = room.height;
    const innerW = panelW - 40;
    const innerH = panelH - 60; // leave room for header
    const tSz = Math.min(Math.floor(innerW / mapW), Math.floor(innerH / mapH));
    const baseOX = panelX + (panelW - mapW * tSz) / 2 + ui.mapPanX;
    const baseOY = panelY + 30 + (innerH - mapH * tSz) / 2 + ui.mapPanY;

    // ── Handle drag-to-pan ───────────────────────────────────────────────
    const ptrInPanel = this.mouseX >= panelX && this.mouseX <= panelX + panelW
      && this.mouseY >= panelY && this.mouseY <= panelY + panelH;
    if (ptrInPanel && this.mouseDown && !this._mapDragging) {
      this._mapDragging = true;
      this._mapDragStartX = this.mouseX;
      this._mapDragStartY = this.mouseY;
      this._mapDragPanStartX = ui.mapPanX;
      this._mapDragPanStartY = ui.mapPanY;
    }
    if (this._mapDragging) {
      if (this.mouseDown) {
        ui.mapPanX = this._mapDragPanStartX + (this.mouseX - this._mapDragStartX);
        ui.mapPanY = this._mapDragPanStartY + (this.mouseY - this._mapDragStartY);
      } else {
        this._mapDragging = false;
      }
    }

    // ── Tiles — larger, with elevation and walkability ───────────────────
    for (let ty = 0; ty < mapH; ty++) {
      for (let tx = 0; tx < mapW; tx++) {
        const tile = room.tiles[ty]?.[tx];
        if (!tile) continue;
        const px = baseOX + tx * tSz;
        const py = baseOY + ty * tSz;

        // Base tile color
        const baseColor = MINI_TILE[tile.type] ?? "#3a3a3a";
        ctx.fillStyle = baseColor;
        ctx.fillRect(px, py, tSz - 0.5, tSz - 0.5);

        // Elevation brightness
        if (tile.elevation > 0) {
          ctx.fillStyle = `rgba(255,240,200,${Math.min(0.18, tile.elevation * 0.07)})`;
          ctx.fillRect(px, py, tSz - 0.5, tSz - 0.5);
        }

        // Non-walkable darkening
        if (!tile.walkable) {
          ctx.fillStyle = "rgba(0,0,0,0.3)";
          ctx.fillRect(px, py, tSz - 0.5, tSz - 0.5);
        }

        // Subtle grid line
        ctx.strokeStyle = "rgba(100,80,50,0.1)";
        ctx.lineWidth = 0.5;
        ctx.strokeRect(px, py, tSz - 0.5, tSz - 0.5);
      }
    }

    // ── Furniture — amber rectangles ─────────────────────────────────────
    for (const f of room.furniture) {
      const fx = baseOX + f.tileX * tSz;
      const fy = baseOY + f.tileY * tSz;
      ctx.save();
      ctx.shadowColor = "rgba(184,149,106,0.2)";
      ctx.shadowBlur = 3;
      ctx.fillStyle = "rgba(184,149,106,0.5)";
      ctx.fillRect(fx + 1, fy + 1, tSz - 2, tSz - 2);
      ctx.restore();
    }

    // ── Creatures — red dots with threat indicator ───────────────────────
    const dotR = Math.max(3, tSz * 0.3);
    for (const c of state.creatures) {
      const crx = baseOX + (c.position.tileX + 0.5) * tSz;
      const cry = baseOY + (c.position.tileY + 0.5) * tSz;
      if (c.ownerId === null) {
        // Multi-zone threat detection cone
        const fmCDir = c.direction as string;
        const fmCAngle = DIR_ANGLE[fmCDir] ?? DIR_ANGLE["S"];

        // Outer peripheral
        ctx.save();
        ctx.translate(crx, cry);
        ctx.rotate(fmCAngle);
        const fmOuterLen = tSz * 3.5;
        const fmOuterSpread = tSz * 2.2;
        const fmOuterG = ctx.createLinearGradient(0, 0, fmOuterLen, 0);
        fmOuterG.addColorStop(0, "rgba(208,64,64,0.04)");
        fmOuterG.addColorStop(1, "rgba(208,64,64,0)");
        ctx.fillStyle = fmOuterG;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(fmOuterLen, -fmOuterSpread);
        ctx.lineTo(fmOuterLen, fmOuterSpread);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Inner aggro zone — tight, pulsing
        const fmAggroPulse = 0.07 + Math.sin(now * 0.004 + c.position.tileX * 3) * 0.03;
        ctx.save();
        ctx.translate(crx, cry);
        ctx.rotate(fmCAngle);
        const fmInnerLen = tSz * 2.2;
        const fmInnerSpread = tSz * 0.9;
        const fmInnerG = ctx.createLinearGradient(0, 0, fmInnerLen, 0);
        fmInnerG.addColorStop(0, `rgba(240,80,48,${fmAggroPulse})`);
        fmInnerG.addColorStop(1, "rgba(240,80,48,0)");
        ctx.fillStyle = fmInnerG;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(fmInnerLen, -fmInnerSpread);
        ctx.lineTo(fmInnerLen, fmInnerSpread);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        const pulse = 0.6 + Math.sin(now * 0.005 + c.position.tileX * 7) * 0.4;
        ctx.save();
        ctx.shadowColor = "rgba(208,64,64,0.35)";
        ctx.shadowBlur = 4;
        ctx.globalAlpha = pulse;
        ctx.fillStyle = "#d04040";
        ctx.beginPath();
        ctx.arc(crx, cry, dotR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Name label
        ctx.save();
        ctx.font = "8px monospace";
        ctx.fillStyle = "rgba(220,80,60,0.7)";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(c.name, crx, cry + dotR + 2);
        ctx.restore();
      } else {
        ctx.fillStyle = "#50b060";
        ctx.beginPath();
        ctx.arc(crx, cry, dotR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── NPCs — green circles with name + quest markers ──────────────────
    for (const npc of state.npcs) {
      const nx = baseOX + (npc.position.tileX + 0.5) * tSz;
      const ny = baseOY + (npc.position.tileY + 0.5) * tSz;
      const nr = Math.max(4, tSz * 0.35);

      // Green dot with glow
      ctx.save();
      ctx.shadowColor = "rgba(96,200,72,0.3)";
      ctx.shadowBlur = 4;
      ctx.fillStyle = "#60c848";
      ctx.beginPath();
      ctx.arc(nx, ny, nr, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Name + title
      ctx.save();
      ctx.font = "bold 8px monospace";
      ctx.fillStyle = "#60c848";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(npc.name, nx, ny + nr + 2);
      ctx.font = "7px monospace";
      ctx.fillStyle = "rgba(192,160,96,0.6)";
      ctx.fillText(npc.title, nx, ny + nr + 12);
      ctx.restore();

      // Quest marker
      const mark = npc.questMark;
      if (mark) {
        const isGold = mark === "available" || mark === "ready_complete";
        const markColor = isGold ? "#f0c860" : "#c0c0c0";
        const markGlyph = mark === "available" ? "!" : "?";
        const markPulse = isGold ? 0.8 + Math.sin(now * 0.006) * 0.2 : 0.7;
        ctx.save();
        ctx.globalAlpha = markPulse;
        if (isGold) {
          ctx.shadowColor = "rgba(240,200,96,0.6)";
          ctx.shadowBlur = 6;
        }
        ctx.font = `bold ${Math.max(10, Math.round(nr * 1.5))}px sans-serif`;
        ctx.fillStyle = markColor;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(markGlyph, nx, ny - nr - 2);
        ctx.restore();
      }
    }

    // ── NPC multi-zone vision cones on full map ────────────────────────
    for (const npc of state.npcs) {
      const fvx = baseOX + (npc.position.tileX + 0.5) * tSz;
      const fvy = baseOY + (npc.position.tileY + 0.5) * tSz;
      const nfocus = npc.gazeFocus ?? 0;
      const fmNpcGA = npc.gazeAngle ?? 0;

      // Peripheral
      ctx.save();
      ctx.translate(fvx, fvy);
      ctx.rotate(fmNpcGA);
      const fmPL = tSz * 4;
      const fmPS = tSz * 2.5;
      const fmPG = ctx.createLinearGradient(0, 0, fmPL, 0);
      fmPG.addColorStop(0, "rgba(96,200,72,0.035)");
      fmPG.addColorStop(1, "rgba(96,200,72,0)");
      ctx.fillStyle = fmPG;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(fmPL, -fmPS);
      ctx.lineTo(fmPL, fmPS);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Active focus
      const fmFL = tSz * (3 + nfocus * 1.5);
      const fmFS = tSz * (1.2 - nfocus * 0.6);
      const fmFA = 0.05 + nfocus * 0.15;
      ctx.save();
      ctx.translate(fvx, fvy);
      ctx.rotate(fmNpcGA);
      const fmFG = ctx.createLinearGradient(0, 0, fmFL, 0);
      fmFG.addColorStop(0, `rgba(96,200,72,${fmFA})`);
      fmFG.addColorStop(0.7, `rgba(96,200,72,${fmFA * 0.3})`);
      fmFG.addColorStop(1, "rgba(96,200,72,0)");
      ctx.fillStyle = fmFG;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(fmFL, -fmFS);
      ctx.lineTo(fmFL, fmFS);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // ── Player — large gold diamond with facing + attention cones ────────
    const ppx = baseOX + (state.player.position.tileX + 0.5) * tSz;
    const ppy = baseOY + (state.player.position.tileY + 0.5) * tSz;

    // 1. Body facing cone
    const pDir = state.player.direction as string;
    const pAngle = DIR_ANGLE[pDir] ?? DIR_ANGLE["S"];
    ctx.save();
    ctx.translate(ppx, ppy);
    ctx.rotate(pAngle);
    const fmBodyLen = tSz * 3;
    const fmBodySpread = tSz * 1.5;
    const fmBodyGrad = ctx.createLinearGradient(0, 0, fmBodyLen, 0);
    fmBodyGrad.addColorStop(0, "rgba(240,200,96,0.22)");
    fmBodyGrad.addColorStop(1, "rgba(240,200,96,0)");
    ctx.fillStyle = fmBodyGrad;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(fmBodyLen, -fmBodySpread);
    ctx.lineTo(fmBodyLen, fmBodySpread);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // 2. Eye/attention cone
    const fmGazeAngle = state.player.gazeAngle ?? pAngle;
    const fmGazeFocus = state.player.gazeFocus ?? 0;
    const fmGazeLen = tSz * (3.5 + fmGazeFocus * 2);
    const fmGazeSpread = tSz * (1.4 - fmGazeFocus * 0.8);
    const fmGazeA = 0.12 + fmGazeFocus * 0.18;
    ctx.save();
    ctx.translate(ppx, ppy);
    ctx.rotate(fmGazeAngle);
    const fmGG = ctx.createLinearGradient(0, 0, fmGazeLen, 0);
    fmGG.addColorStop(0, `rgba(180,240,255,${fmGazeA})`);
    fmGG.addColorStop(0.6, `rgba(180,240,255,${fmGazeA * 0.35})`);
    fmGG.addColorStop(1, "rgba(180,240,255,0)");
    ctx.fillStyle = fmGG;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(fmGazeLen, -fmGazeSpread);
    ctx.lineTo(fmGazeLen, fmGazeSpread);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Player diamond
    const pd = Math.max(4, tSz * 0.4);
    const playerPulse = 0.85 + Math.sin(now * 0.004) * 0.15;
    ctx.save();
    ctx.shadowColor = `rgba(240,200,96,${playerPulse * 0.6})`;
    ctx.shadowBlur = 8;
    ctx.fillStyle = W.goldGlow;
    ctx.beginPath();
    ctx.moveTo(ppx, ppy - pd);
    ctx.lineTo(ppx + pd, ppy);
    ctx.lineTo(ppx, ppy + pd);
    ctx.lineTo(ppx - pd, ppy);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#fff8e0";
    ctx.beginPath();
    ctx.arc(ppx, ppy, pd * 0.25, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Player label
    ctx.save();
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = W.goldGlow;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(state.player.name, ppx, ppy + pd + 2);
    ctx.restore();

    ctx.restore(); // un-clip

    // ── Header — room name centered at top of panel ─────────────────────
    ctx.save();
    ctx.font = "bold 16px 'Georgia', serif";
    ctx.fillStyle = W.goldBright;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(room.name ?? "Map", panelX + panelW / 2, panelY + 8);
    // Decorative line under title
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const titleW = ctx.measureText(room.name ?? "Map").width;
    ctx.moveTo(panelX + panelW / 2 - titleW / 2 - 10, panelY + 26);
    ctx.lineTo(panelX + panelW / 2 + titleW / 2 + 10, panelY + 26);
    ctx.stroke();
    ctx.restore();

    // ── Close button — top-right corner ─────────────────────────────────
    const closeSz = 24;
    const closeX = panelX + panelW - closeSz - 8;
    const closeY = panelY + 6;
    ctx.save();
    ctx.beginPath();
    ctx.arc(closeX + closeSz / 2, closeY + closeSz / 2, closeSz / 2, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(40,30,15,0.8)";
    ctx.fill();
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.font = "bold 14px sans-serif";
    ctx.fillStyle = W.textDim;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("×", closeX + closeSz / 2, closeY + closeSz / 2);
    ctx.restore();

    this.addHit(closeX, closeY, closeSz, closeSz, (s) => {
      s.ui.mapOpen = false;
      s.ui.uiSounds.push("ui_close");
    });

    // ── Hint text — bottom of panel ─────────────────────────────────────
    ctx.save();
    ctx.font = "9px 'Georgia', serif";
    ctx.fillStyle = "rgba(160,140,100,0.4)";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText("Drag to pan  •  Click tile to move  •  M or Esc to close", panelX + panelW / 2, panelY + panelH - 6);
    ctx.restore();

    // ── Click-to-navigate on the full map ────────────────────────────────
    const fmOX = baseOX, fmOY = baseOY, fmTSz = tSz;
    this.addHit(panelX + 4, panelY + 30, panelW - 8, panelH - 40, (s) => {
      // Only navigate if pointer barely moved (not a drag)
      const dragDist = Math.abs(ui.mapPanX - this._mapDragPanStartX) + Math.abs(ui.mapPanY - this._mapDragPanStartY);
      if (dragDist > 5) return;
      const tileX = Math.floor((this.mouseX - fmOX) / fmTSz);
      const tileY = Math.floor((this.mouseY - fmOY) / fmTSz);
      const rm = s.currentRoom;
      if (!rm) return;
      if (tileX < 0 || tileX >= rm.width || tileY < 0 || tileY >= rm.height) return;
      const tile = rm.tiles[tileY]?.[tileX];
      if (!tile || !tile.walkable) return;
      s.ui.clickIndicator = { tileX, tileY, time: performance.now(), reachable: true };
      s.player.targetPosition = { tileX, tileY };
      s.player.movementQueue = [{ tileX, tileY }];
      s.ui.uiSounds.push("ui_click");
      s.ui.mapOpen = false;
    });
  }

  // ─── Inventory Panel ─────────────────────────────────────────────────────────

  private drawInventoryPanel(state: GameState, SW: number, SH: number): void {
    const { ctx } = this;
    const pW = Math.min(SW - 20, 484);
    const pX = (SW - pW) / 2;
    const pY = SH - BAR_H - BAR_PAD_Y - PANEL_H - PANEL_GAP;

    this.drawWoodPanel(pX, pY, pW, PANEL_H, 10);

    // ── Header ──
    ctx.save();
    ctx.font = "bold 14px monospace";
    ctx.fillStyle = W.textPrime;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText("Inventory", pX + 16, pY + 20);
    ctx.restore();

    // Close ×
    const cSz = 22, cX = pX + pW - cSz - 10, cY = pY + 9;
    this.drawCloseButton(cX, cY, cSz);
    this.addHit(cX, cY, cSz, cSz, (s) => { s.ui.inventoryOpen = false; s.ui.uiSounds.push("ui_close"); });

    // Amber separator line under header
    ctx.save();
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pX + 10, pY + 32);
    ctx.lineTo(pX + pW - 10, pY + 32);
    ctx.stroke();
    ctx.restore();

    // ── Tabs ──
    type TabEntry = { label: string; key: string; val: import("../engine/state.js").InventoryTab };
    const tabs: TabEntry[] = [
      { label: "All",       key: "1", val: "all"       },
      { label: "Furniture", key: "2", val: "furniture" },
      { label: "Clothing",  key: "3", val: "clothing"  },
      { label: "Gear",      key: "4", val: "equipment" },
      { label: "Materials", key: "5", val: "materials" },
    ];
    const tabH  = 26;
    const tabY  = pY + 38;
    const tabTW = (pW - 24) / tabs.length;

    for (let i = 0; i < tabs.length; i++) {
      const { label, key, val } = tabs[i];
      const tx     = pX + 12 + i * tabTW;
      const active = (state.ui.inventoryTab ?? "all") === val;
      this.drawTab(tx, tabY, tabTW - 3, tabH, label, active);
      // Key hint — small number in top-right corner of tab
      ctx.save();
      ctx.font = "7px monospace";
      ctx.fillStyle = active ? W.woodDark : W.textMuted;
      ctx.globalAlpha = 0.6;
      ctx.textAlign = "right";
      ctx.textBaseline = "top";
      ctx.fillText(key, tx + tabTW - 7, tabY + 3);
      ctx.restore();
      const tv = val;
      this.addHit(tx, tabY, tabTW - 3, tabH, (s) => {
        s.ui.inventoryTab = tv;
        this.invPage = 0;
        s.ui.uiSounds.push("ui_tab");
      });
    }

    // ── Item grid ──
    const curTab = state.ui.inventoryTab ?? "all";
    const items = state.player.inventory.filter((it) => {
      if (curTab === "all")       return true;
      if (curTab === "furniture") return it.type === "furniture";
      if (curTab === "clothing")  return it.type === "clothing";
      if (curTab === "equipment") return it.type === "equipment";
      if (curTab === "materials") return it.type === "material";
      return true;
    });

    const COLS  = 5;
    const iGap  = 6;
    const iW    = Math.floor((pW - 24 - iGap * (COLS - 1)) / COLS);
    const iH    = 68;
    const gY0   = tabY + tabH + 10;
    const gYMax = pY + PANEL_H - 38;

    const maxRows = Math.max(1, Math.floor((gYMax - gY0) / (iH + iGap)));
    const perPage = COLS * maxRows;
    const pages   = Math.max(1, Math.ceil(items.length / perPage));
    if (this.invPage >= pages) this.invPage = 0;
    const start   = this.invPage * perPage;

    if (items.length === 0) {
      ctx.save();
      ctx.font = "italic 12px monospace";
      ctx.fillStyle = W.textMuted;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Nothing here yet.", pX + pW / 2, gY0 + 40);
      ctx.restore();
    }

    for (let i = 0; i < Math.min(items.length - start, perPage); i++) {
      const item   = items[start + i];
      const col    = i % COLS;
      const row    = Math.floor(i / COLS);
      const ix     = pX + 12 + col * (iW + iGap);
      const iy     = gY0 + row * (iH + iGap);
      const rar    = RARITY_COLORS[item.rarity] ?? W.rarCommon;
      const canPlace = item.type === "furniture" && !!item.furnitureTypeId;

      this.drawItemSlot(ix, iy, iW, iH, item.name, rar, item.quantity, canPlace, item.type, item.furnitureTypeId);

      // Hover detection for item glow + tooltip
      const itemHovered = this.mouseX >= ix && this.mouseX <= ix + iW &&
                          this.mouseY >= iy && this.mouseY <= iy + iH;
      if (itemHovered) {
        // Warm amber hover glow on the slot border
        ctx.save();
        this.rr(ix - 1, iy - 1, iW + 2, iH + 2, 6);
        ctx.strokeStyle = W.goldBright;
        ctx.lineWidth = 2;
        ctx.shadowColor = W.goldGlow;
        ctx.shadowBlur = 8;
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.restore();
      }
      if (itemHovered) {
        // Build tooltip text: "Name\ndetails..."
        let tipText = item.name;
        const rarLabel = item.rarity.charAt(0).toUpperCase() + item.rarity.slice(1);
        if (item.equipmentData) {
          const eq = item.equipmentData;
          const slotLabel = eq.slot.charAt(0).toUpperCase() + eq.slot.slice(1);
          tipText += `\n${rarLabel} ${slotLabel}`;
          const stats: string[] = [];
          if (eq.attackBonus > 0) stats.push(`+${eq.attackBonus} ATK`);
          if (eq.defenseBonus > 0) stats.push(`+${eq.defenseBonus} DEF`);
          if (eq.hpBonus > 0) stats.push(`+${eq.hpBonus} HP`);
          if (stats.length) tipText += `\n${stats.join("  ")}`;
          if (eq.levelReq > 0) tipText += `\nReq: Level ${eq.levelReq}`;
          if (eq.description) tipText += `\n${eq.description}`;
        } else if (item.type === "furniture") {
          tipText += `\n${rarLabel} Furniture · Click to place`;
        } else if (item.type === "material") {
          tipText += `\n${rarLabel} Material`;
        } else if (item.type === "consumable") {
          tipText += `\n${rarLabel} Consumable`;
        } else {
          tipText += `\n${rarLabel} ${item.type.charAt(0).toUpperCase() + item.type.slice(1)}`;
        }
        if (item.quantity > 1) tipText += `\n×${item.quantity}`;
        this.tooltipZones.push({ x: ix, y: iy, w: iW, h: iH, text: tipText });
      }

      if (canPlace) {
        const ftid = item.furnitureTypeId!;
        this.addHit(ix, iy, iW, iH, (s) => {
          s.ui.placingFurnitureType = ftid;
          s.ui.placingDirection     = "SE";
          s.ui.inventoryOpen        = false;
          s.ui.furnitureContextId   = null;
          s.ui.selectedFurniture    = null;
          s.ui.editMode             = true;  // ensure build mode is active
          s.ui.combatStance         = false;
          s.ui.uiSounds.push("ui_open");
        });
      } else if (item.type === "equipment" && item.equipmentData) {
        const itemId = item.id;
        this.addHit(ix, iy, iW, iH, (s) => {
          const invItem = s.player.inventory.find(it => it.id === itemId);
          if (!invItem || !invItem.equipmentData) return;
          const eqData = invItem.equipmentData;
          // Check level requirement
          if (s.player.combat.level < eqData.levelReq) {
            s.ui.uiSounds.push("ui_error");
            addToast(s, `Requires level ${eqData.levelReq}`, "info", 2.5);
            return;
          }
          const slot = eqData.slot;
          const currentlyEquipped = s.player.equipment[slot];
          // Unequip current item in that slot → return to inventory
          if (currentlyEquipped) {
            s.player.inventory.push(currentlyEquipped);
          }
          // Remove the new item from inventory and equip it
          s.player.inventory = s.player.inventory.filter(it => it.id !== itemId);
          s.player.equipment[slot] = invItem;
          s.ui.uiSounds.push("ui_equip");
          addToast(s, `Equipped ${invItem.name}`, "loot", 1.5);
        });
      }
    }

    // ── Pagination ──
    const pgY = pY + PANEL_H - 28;
    const pgCX = pX + pW / 2;
    if (pages > 1) {
      const arrowW = 28, arrowH = 20;
      const prevX = pgCX - 50;
      const nextX = pgCX + 22;

      this.drawWoodButton("◀", "", prevX, pgY, arrowW, arrowH, false);
      this.drawWoodButton("▶", "", nextX, pgY, arrowW, arrowH, false);
      this.addHit(prevX, pgY, arrowW, arrowH, (s) => {
        this.invPage = (this.invPage - 1 + pages) % pages;
        s.ui.uiSounds.push("ui_tab");
      });
      this.addHit(nextX, pgY, arrowW, arrowH, (s) => {
        this.invPage = (this.invPage + 1) % pages;
        s.ui.uiSounds.push("ui_tab");
      });

      ctx.save();
      ctx.font = "11px monospace";
      ctx.fillStyle = W.textDim;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${this.invPage + 1} / ${pages}`, pgCX, pgY + 10);
      ctx.restore();
    }
  }

  // ─── Edit Mode Banner ──────────────────────────────────────────────────────

  private drawEditModeBanner(state: GameState, SW: number): void {
    const { ctx } = this;
    const bannerW = 220;
    const bannerH = 36;
    const bx = Math.round((SW - bannerW) / 2);
    const by = 10;

    // Panel background — dark with blue accent
    const grad = ctx.createLinearGradient(bx, by, bx, by + bannerH);
    grad.addColorStop(0, "#0f1a2e");
    grad.addColorStop(1, "#0a1220");
    ctx.fillStyle = grad;
    roundRect(ctx, bx, by, bannerW, bannerH, 8);
    ctx.fill();

    // Border — blue accent
    ctx.strokeStyle = "#3a7bd5";
    ctx.lineWidth = 1.5;
    roundRect(ctx, bx, by, bannerW, bannerH, 8);
    ctx.stroke();

    // Inner glow
    ctx.strokeStyle = "rgba(80, 140, 220, 0.15)";
    ctx.lineWidth = 1;
    roundRect(ctx, bx + 2, by + 2, bannerW - 4, bannerH - 4, 6);
    ctx.stroke();

    // Title text
    ctx.font = "bold 13px monospace";
    ctx.fillStyle = "#7ab3f0";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🔨  Build Mode", bx + bannerW / 2 - 16, by + bannerH / 2);

    // Exit hint (right side)
    ctx.font = "9px monospace";
    ctx.fillStyle = "rgba(120, 160, 200, 0.6)";
    ctx.textAlign = "right";
    ctx.fillText("P / ESC", bx + bannerW - 12, by + bannerH / 2);

    // Register hit zone for the whole banner — clicking it exits build mode
    this.addHit(bx, by, bannerW, bannerH, (s) => {
      s.ui.editMode = false;
      s.ui.selectedFurniture = null;
      s.ui.furnitureContextId = null;
      s.ui.placingFurnitureType = null;
      s.ui.movingFurnitureId = null;
      s.ui.inventoryOpen = false;
      s.ui.uiSounds.push("ui_close");
    });
  }

  // ─── Combat Stance Banner ─────────────────────────────────────────────────────

  private drawCombatStanceBanner(_state: GameState, SW: number): void {
    const { ctx } = this;
    const bannerW = 220;
    const bannerH = 36;
    const bx = Math.round((SW - bannerW) / 2);
    const by = 10;

    // Panel background — dark with red accent
    const grad = ctx.createLinearGradient(bx, by, bx, by + bannerH);
    grad.addColorStop(0, "#2e0f0f");
    grad.addColorStop(1, "#200a0a");
    ctx.fillStyle = grad;
    roundRect(ctx, bx, by, bannerW, bannerH, 8);
    ctx.fill();

    // Border — red accent
    ctx.strokeStyle = "#d53a3a";
    ctx.lineWidth = 1.5;
    roundRect(ctx, bx, by, bannerW, bannerH, 8);
    ctx.stroke();

    // Inner glow
    ctx.strokeStyle = "rgba(220, 80, 80, 0.15)";
    ctx.lineWidth = 1;
    roundRect(ctx, bx + 2, by + 2, bannerW - 4, bannerH - 4, 6);
    ctx.stroke();

    // Title text
    ctx.font = "bold 13px monospace";
    ctx.fillStyle = "#f07a7a";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("\u2694  Combat Stance", bx + bannerW / 2 - 10, by + bannerH / 2);

    // Exit hint (right side)
    ctx.font = "9px monospace";
    ctx.fillStyle = "rgba(200, 120, 120, 0.6)";
    ctx.textAlign = "right";
    ctx.fillText("C / ESC", bx + bannerW - 12, by + bannerH / 2);

    // Register hit zone — clicking exits combat stance
    this.addHit(bx, by, bannerW, bannerH, (s) => {
      s.ui.combatStance = false;
      s.player.animState = "idle";
      s.ui.uiSounds.push("ui_close");
    });
  }

  // ─── Placement Banner ────────────────────────────────────────────────────────

  private drawPlacementBanner(state: GameState, SW: number): void {
    const { ctx } = this;
    const bW = Math.min(SW - 24, 480);
    const bH = 44;
    const bX = (SW - bW) / 2;
    const bY = 10;

    // Panel background with gradient
    ctx.save();
    this.rr(bX, bY, bW, bH, 10);
    const grad = ctx.createLinearGradient(bX, bY, bX, bY + bH);
    grad.addColorStop(0, "#1a1208");
    grad.addColorStop(0.5, "#201810");
    grad.addColorStop(1, "#140e06");
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = W.placeEdge;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Inner highlight
    this.rr(bX + 2, bY + 2, bW - 4, bH - 4, 8);
    ctx.strokeStyle = "rgba(212, 160, 48, 0.15)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    const moving = !!state.ui.movingFurnitureId;
    const rawName = moving
      ? (state.currentRoom?.furniture.find(f => f.id === state.ui.movingFurnitureId)?.type ?? "")
      : (state.ui.placingFurnitureType ?? "");

    // Pretty name: sofa_oak → Sofa Oak
    const ft = getFurnitureType(rawName);
    const prettyName = ft?.name ?? rawName.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

    // Mode badge (left side)
    const badgeLabel = moving ? "MOVE" : "PLACE";
    const badgeColor = moving ? "#4a80a0" : "#6a8040";
    ctx.save();
    ctx.font = "bold 9px monospace";
    const badgeW = ctx.measureText(badgeLabel).width + 12;
    const badgeH = 18;
    const badgeX = bX + 10;
    const badgeY = bY + (bH - badgeH) / 2;
    this.rr(badgeX, badgeY, badgeW, badgeH, 4);
    ctx.fillStyle = badgeColor;
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(badgeLabel, badgeX + badgeW / 2, badgeY + badgeH / 2);
    ctx.restore();

    // Furniture name
    ctx.save();
    ctx.font = "bold 13px monospace";
    ctx.fillStyle = W.placeText;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(prettyName, badgeX + badgeW + 10, bY + bH / 2);
    ctx.restore();

    // Direction compass rose (right side)
    const compassX = bX + bW - 58;
    const compassY = bY + bH / 2;
    const dir = state.ui.placingDirection;
    const cx4  = ["SE","SW","NW","NE"] as const;
    ctx.save();
    // Compass ring
    ctx.strokeStyle = "rgba(212, 160, 48, 0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(compassX, compassY, 14, 7, 0, 0, Math.PI * 2);
    ctx.stroke();
    for (const d of cx4) {
      const angle = (["NE","SE","SW","NW"].indexOf(d as "NE"|"SE"|"SW"|"NW")) * (Math.PI / 2) - Math.PI / 4;
      const rx = compassX + Math.cos(angle) * 14;
      const ry = compassY + Math.sin(angle) * 7;
      const active = d === dir;
      ctx.beginPath();
      ctx.arc(rx, ry, active ? 5 : 2.5, 0, Math.PI * 2);
      ctx.fillStyle = active ? W.goldBright : W.textMuted;
      ctx.fill();
      if (active) {
        ctx.strokeStyle = W.goldGlow;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    // direction label
    ctx.font = "bold 10px monospace";
    ctx.fillStyle = W.goldGlow;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(dir, compassX, compassY);
    ctx.restore();

    // Keyboard hints (left of compass)
    ctx.save();
    ctx.font = "10px monospace";
    ctx.fillStyle = W.placeHint;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("R rotate · Esc cancel", compassX - 20, bY + bH / 2);
    ctx.restore();
  }

  // ─── Context Menu ────────────────────────────────────────────────────────────

  private drawContextMenu(state: GameState, SW: number, SH: number): void {
    const { ctx } = this;
    const fid = state.ui.furnitureContextId!;
    const placement = state.currentRoom?.furniture.find(f => f.id === fid);
    if (!placement) {
      state.ui.furnitureContextId = null;
      return;
    }

    const items: Array<{ label: string; icon: string; key?: string; danger?: boolean; action: (s: GameState) => void }> = [
      {
        label: "Rotate",
        icon: "↻",
        key: "R",
        action: (s) => {
          const room = s.currentRoom;
          const p = room?.furniture.find(f => f.id === fid);
          if (p && room) {
            // Clear old footprint tiles before rotation
            const oldFP = getFootprintTiles(p);
            for (const t of oldFP) {
              const tile = room.tiles[t.y]?.[t.x];
              if (tile) { tile.furniture = undefined; tile.walkable = true; }
            }
            const cycle: import("../engine/state.js").Direction[] = ["SE","SW","NW","NE"];
            const idx = cycle.indexOf(p.direction as "SE"|"SW"|"NW"|"NE");
            p.direction = cycle[(idx + 1) % cycle.length];
            // Mark new footprint tiles after rotation
            const newFP = getFootprintTiles(p);
            for (const t of newFP) {
              const tile = room.tiles[t.y]?.[t.x];
              if (tile) { tile.furniture = p.id; tile.walkable = false; }
            }
          }
          s.ui.furnitureContextId = null;
          s.ui.editMode = true;  // rotating means we're in build mode
          s.ui.combatStance = false;
          s.ui.uiSounds.push("ui_click");
        },
      },
      {
        label: "Move",
        icon: "↔",
        key: "M",
        action: (s) => {
          const room = s.currentRoom;
          const p = room?.furniture.find(f => f.id === fid);
          if (p && room) {
            s.ui.movingFurnitureId    = fid;
            s.ui.movingOriginalTile   = { tileX: p.tileX, tileY: p.tileY };
            s.ui.placingFurnitureType = p.type;
            s.ui.placingDirection     = p.direction as import("../engine/state.js").Direction;
            // Clear all footprint tiles so they show as available
            const fp = getFootprintTiles(p);
            for (const t of fp) {
              const tile = room.tiles[t.y]?.[t.x];
              if (tile) { tile.furniture = undefined; tile.walkable = true; }
            }
          }
          s.ui.furnitureContextId = null;
          s.ui.editMode = true;  // ensure build mode
          s.ui.combatStance = false;
          s.ui.uiSounds.push("ui_click");
        },
      },
      {
        label: "Pick Up",
        icon: "▲",
        key: "G",
        action: (s) => {
          const room = s.currentRoom;
          if (!room) return;
          const idx = room.furniture.findIndex(f => f.id === fid);
          if (idx === -1) return;
          const p = room.furniture[idx];
          // Clear all footprint tiles
          const fp = getFootprintTiles(p);
          for (const t of fp) {
            const tile = room.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = undefined; tile.walkable = true; }
          }
          room.furniture.splice(idx, 1);
          // Add back to inventory
          const existing = s.player.inventory.find(
            it => it.type === "furniture" && it.furnitureTypeId === p.type
          );
          if (existing) {
            existing.quantity += 1;
          } else {
            s.player.inventory.push({
              id:             `inv_pickup_${Date.now()}`,
              type:           "furniture",
              name:           p.type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
              quantity:       1,
              rarity:         "common",
              acquiredAt:     Date.now(),
              furnitureTypeId: p.type,
            });
          }
          s.ui.furnitureContextId = null;
          s.ui.combatStance = false;
          s.ui.uiSounds.push("ui_click");
        },
      },
      {
        label: "Cancel",
        icon: "✕",
        key: "Esc",
        danger: false,
        action: (s) => { s.ui.furnitureContextId = null; s.ui.uiSounds.push("ui_close"); },
      },
    ];

    // Furniture name header
    const ft = getFurnitureType(placement.type);
    const fName = ft?.name ?? placement.type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    const HEADER_H = 26;

    const menuH = HEADER_H + items.length * CTX_ROW_H + 10;
    let mx = Math.min(state.ui.furnitureContextX, SW - CTX_W - 8);
    let my = state.ui.furnitureContextY;
    if (my + menuH > SH - 8) my = SH - menuH - 8;
    if (mx < 8) mx = 8;

    // Panel background
    ctx.save();
    this.rr(mx, my, CTX_W, menuH, 8);
    ctx.fillStyle = W.ctxBg;
    ctx.fill();
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Inner glow
    this.rr(mx + 2, my + 2, CTX_W - 4, menuH - 4, 6);
    ctx.strokeStyle = "rgba(180, 140, 40, 0.15)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // Header: furniture name + direction
    ctx.save();
    ctx.font = "bold 10px monospace";
    ctx.fillStyle = W.goldBright;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    const truncName = fName.length > 14 ? fName.slice(0, 13) + "…" : fName;
    ctx.fillText(truncName, mx + 10, my + HEADER_H / 2 + 1);
    // Direction indicator
    ctx.font = "8px monospace";
    ctx.fillStyle = W.textDim;
    ctx.textAlign = "right";
    ctx.fillText(placement.direction, mx + CTX_W - 10, my + HEADER_H / 2 + 1);
    // Separator line
    ctx.strokeStyle = W.goldDark;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(mx + 8, my + HEADER_H);
    ctx.lineTo(mx + CTX_W - 8, my + HEADER_H);
    ctx.stroke();
    ctx.restore();

    for (let i = 0; i < items.length; i++) {
      const item  = items[i];
      const ry    = my + HEADER_H + 5 + i * CTX_ROW_H;
      const isLast = i === items.length - 1;

      // Hover detection for this row
      const rowHovered = this.mouseX >= mx + 4 && this.mouseX <= mx + CTX_W - 4 &&
                         this.mouseY >= ry + 2 && this.mouseY <= ry + CTX_ROW_H - 2;

      // Row background — warm highlight, brighter on hover
      ctx.save();
      if (rowHovered && !isLast) {
        // Active hover: amber glow fill + subtle left accent
        ctx.fillStyle = "rgba(212,160,48,0.18)";
        this.rr(mx + 4, ry + 2, CTX_W - 8, CTX_ROW_H - 4, 4);
        ctx.fill();
        // Left accent bar
        ctx.fillStyle = W.goldBright;
        this.rr(mx + 4, ry + 4, 2.5, CTX_ROW_H - 8, 1);
        ctx.fill();
      } else if (rowHovered && isLast) {
        // Cancel hover: muted red tint
        ctx.fillStyle = "rgba(180,60,40,0.14)";
        this.rr(mx + 4, ry + 2, CTX_W - 8, CTX_ROW_H - 4, 4);
        ctx.fill();
      } else {
        ctx.fillStyle = "rgba(212,160,48,0.06)";
        this.rr(mx + 4, ry + 2, CTX_W - 8, CTX_ROW_H - 4, 4);
        ctx.fill();
      }
      ctx.restore();

      // Separator above last item (Cancel)
      if (isLast) {
        ctx.save();
        ctx.strokeStyle = W.goldDark;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(mx + 8, ry - 1);
        ctx.lineTo(mx + CTX_W - 8, ry - 1);
        ctx.stroke();
        ctx.restore();
      }

      // Icon — brighter on hover
      ctx.save();
      ctx.font = "bold 14px monospace";
      const iconColor = isLast
        ? (rowHovered ? "#d06050" : W.textMuted)
        : (rowHovered ? W.goldGlow : W.goldBright);
      ctx.fillStyle = iconColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(item.icon, mx + 20, ry + CTX_ROW_H / 2);
      // Label — brighter on hover
      ctx.font = isLast ? "11px monospace" : "bold 12px monospace";
      const labelColor = isLast
        ? (rowHovered ? "#d06050" : W.textMuted)
        : (rowHovered ? W.goldGlow : W.textPrime);
      ctx.fillStyle = labelColor;
      ctx.textAlign = "left";
      ctx.fillText(item.label, mx + 36, ry + CTX_ROW_H / 2);
      // Key hint (right-aligned, dimmed)
      if (item.key) {
        ctx.font = "7px monospace";
        ctx.fillStyle = rowHovered ? W.goldBright : W.textDim;
        ctx.textAlign = "right";
        ctx.fillText(item.key, mx + CTX_W - 12, ry + CTX_ROW_H / 2);
      }
      ctx.restore();

      const ia = item.action;
      this.addHit(mx, ry + 2, CTX_W, CTX_ROW_H - 4, ia);
    }

    // Click-outside dismissal zone: register a low-priority full-screen hit
    // that only fires if NOTHING above matched (hitZones tested in order).
    // We push it AFTER the menu items so menu clicks win.
    this.addHit(0, 0, SW, SH, (s) => { s.ui.furnitureContextId = null; });
  }

  // ─── Drawing Primitives ──────────────────────────────────────────────────────

  /**
   * Procedural aged-wood panel: gradient base + grain lines + amber border.
   */
  private drawWoodPanel(x: number, y: number, w: number, h: number, r: number): void {
    const { ctx } = this;

    ctx.save();
    this.rr(x, y, w, h, r);
    ctx.clip();

    // Base gradient — darker at top edges, lighter in centre
    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0,   W.woodDark);
    grad.addColorStop(0.3, W.woodMid);
    grad.addColorStop(0.7, W.woodMid);
    grad.addColorStop(1,   W.woodDark);
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    // Horizontal wood grain (subtle, 3-5px apart)
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 0.75;
    let gy = 0;
    while (gy < h) {
      const wave = Math.sin(gy * 0.08 + x * 0.03) * 2;
      ctx.beginPath();
      ctx.moveTo(x,     y + gy + wave);
      ctx.lineTo(x + w, y + gy + Math.sin((gy + 40) * 0.08) * 2);
      ctx.stroke();
      gy += 3 + Math.abs(Math.sin(gy * 0.7)) * 2;
    }

    // Diagonal highlight (top-left to centre) — like light catching the wood
    ctx.globalAlpha = 0.07;
    const shine = ctx.createLinearGradient(x, y, x + w * 0.6, y + h * 0.4);
    shine.addColorStop(0,   "#f0c860");
    shine.addColorStop(1,   "transparent");
    ctx.fillStyle = shine;
    ctx.fillRect(x, y, w, h);

    ctx.restore();

    // Outer border: dark gold bottom-right, bright gold top-left
    ctx.save();
    this.rr(x, y, w, h, r);
    ctx.strokeStyle = W.goldMid;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Top-left inner bevel (bright)
    ctx.strokeStyle = "rgba(212, 160, 48, 0.45)";
    ctx.lineWidth = 1;
    this.rr(x + 1.5, y + 1.5, w - 3, h - 3, r - 1);
    ctx.stroke();

    // Bottom-right shadow (dark)
    ctx.strokeStyle = "rgba(0,0,0,0.55)";
    ctx.lineWidth = 1;
    this.rr(x + 2.5, y + 2.5, w - 5, h - 5, r - 2);
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Slightly raised wood button with amber label.
   */
  private drawWoodButton(
    label: string, sub: string,
    x: number, y: number, w: number, h: number,
    active: boolean
  ): void {
    const { ctx } = this;
    ctx.save();

    // Base fill
    const bg = active ? W.woodLight : W.woodDark;
    this.rr(x, y, w, h, 5);
    ctx.fillStyle = bg;
    ctx.fill();

    // Top highlight (pressed = no highlight)
    if (!active) {
      ctx.fillStyle = "rgba(240, 200, 80, 0.06)";
      ctx.beginPath();
      ctx.rect(x + 1, y + 1, w - 2, h / 3);
      ctx.fill();
    }

    // Border
    ctx.strokeStyle = active ? W.goldBright : W.goldDark;
    ctx.lineWidth = 1.5;
    this.rr(x, y, w, h, 5);
    ctx.stroke();

    // Label
    ctx.font = "bold 11px monospace";
    ctx.fillStyle = active ? W.goldGlow : W.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const labelY = sub ? y + h / 2 - 3 : y + h / 2;
    ctx.fillText(label, x + w / 2, labelY);

    // Sub-label (key hint)
    if (sub) {
      ctx.font = "8px monospace";
      ctx.fillStyle = W.textMuted;
      ctx.fillText(sub, x + w / 2, y + h / 2 + 7);
    }

    ctx.restore();
  }

  /**
   * Sunken inventory slot with a category-appropriate pixel icon.
   */
  private drawItemSlot(
    x: number, y: number, w: number, h: number,
    name: string, rarColor: string, qty: number,
    canPlace: boolean, type: string, furnitureTypeId?: string
  ): void {
    const { ctx } = this;

    // Outer frame (rarity coloured)
    ctx.save();
    this.rr(x, y, w, h, 5);
    ctx.fillStyle = W.slotBg;
    ctx.fill();
    ctx.strokeStyle = rarColor;
    ctx.lineWidth = canPlace ? 1.5 : 1;
    ctx.stroke();
    ctx.restore();

    // Icon area (inset)
    const iconY  = y + 4;
    const iconH  = h - 26;
    ctx.save();
    ctx.fillStyle = W.woodDark;
    this.rr(x + 3, iconY, w - 6, iconH, 3);
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.5)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // Rarity gem pip (top-left of icon area)
    ctx.save();
    ctx.fillStyle = rarColor;
    ctx.beginPath();
    ctx.arc(x + 9, iconY + 8, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Render actual furniture sprite or fall back to category icon
    if (furnitureTypeId) {
      const ft = getFurnitureType(furnitureTypeId);
      if (ft) {
        ctx.save();
        const scale = Math.min(iconH, w - 6) / Math.max(ft.sprite.width, ft.sprite.height, 48) * 0.6;
        ctx.translate(x + w / 2, iconY + iconH / 2 + 2);
        ctx.scale(scale, scale);
        renderFurniture(ctx, ft, 0, 0, 0, { on: true, lit: true }, 0);
        ctx.restore();
      } else {
        this.drawCategoryIcon(x + w / 2, iconY + iconH / 2 + 2, type, rarColor, name);
      }
    } else {
      this.drawCategoryIcon(x + w / 2, iconY + iconH / 2 + 2, type, rarColor, name);
    }

    // "Place" indicator at top-right of icon area
    if (canPlace) {
      ctx.save();
      ctx.fillStyle = W.placeEdge;
      ctx.beginPath();
      ctx.arc(x + w - 8, iconY + 8, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = "bold 7px monospace";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("▼", x + w - 8, iconY + 8);
      ctx.restore();
    }

    // Item name (bottom, truncated)
    const nm = name.length > 11 ? name.slice(0, 10) + "…" : name;
    ctx.save();
    ctx.font = "9px monospace";
    ctx.fillStyle = W.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(nm, x + w / 2, y + h - 11);
    ctx.restore();

    // Qty badge
    if (qty > 1) {
      ctx.save();
      ctx.fillStyle = W.woodMid;
      this.rr(x + w - 20, y + h - 16, 18, 12, 3);
      ctx.fill();
      ctx.font = "bold 8px monospace";
      ctx.fillStyle = W.goldBright;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`×${qty}`, x + w - 11, y + h - 10);
      ctx.restore();
    }
  }

  /**
   * Item-specific pixel icon centred at (cx, cy).
   * Furniture shows isometric silhouettes, clothing shows garment outlines,
   * materials show gem/crystal forms. Everything procedural — zero image deps.
   */
  private drawCategoryIcon(cx: number, cy: number, type: string, color: string, name?: string): void {
    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = color + "a0";
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const n = (name ?? "").toLowerCase();

    if (type === "furniture") {
      if (n.includes("bed")) {
        // Bed top view
        ctx.fillRect(cx - 8, cy - 5, 16, 12);  // mattress
        ctx.fillStyle = color;
        ctx.fillRect(cx - 8, cy - 5, 16, 3);   // headboard
        ctx.fillRect(cx - 6, cy - 3, 5, 3);    // pillow
        ctx.fillRect(cx + 1, cy - 3, 5, 3);    // pillow
      } else if (n.includes("sofa") || n.includes("couch")) {
        ctx.fillRect(cx - 9, cy - 2, 18, 8);   // seat
        ctx.fillRect(cx - 9, cy - 7, 18, 5);   // back
        ctx.fillRect(cx - 10, cy - 4, 3, 10);  // left arm
        ctx.fillRect(cx + 7, cy - 4, 3, 10);   // right arm
      } else if (n.includes("lamp")) {
        // Lamp silhouette
        ctx.beginPath();
        ctx.moveTo(cx - 5, cy - 3);
        ctx.lineTo(cx - 3, cy - 8);
        ctx.lineTo(cx + 3, cy - 8);
        ctx.lineTo(cx + 5, cy - 3);
        ctx.closePath();
        ctx.fill();
        ctx.fillRect(cx - 1, cy - 3, 2, 9);    // pole
        ctx.fillRect(cx - 4, cy + 6, 8, 2);     // base
      } else if (n.includes("shelf") || n.includes("book")) {
        ctx.fillStyle = color + "60";
        ctx.fillRect(cx - 7, cy - 9, 14, 18);   // frame
        ctx.fillStyle = color;
        for (let r = 0; r < 3; r++) {
          const ry = cy - 7 + r * 6;
          ctx.fillRect(cx - 6, ry, 3, 5);
          ctx.fillRect(cx - 2, ry, 3, 5);
          ctx.fillRect(cx + 2, ry, 3, 5);
          ctx.fillRect(cx - 7, ry + 5, 14, 1);
        }
      } else if (n.includes("fire")) {
        ctx.fillStyle = color + "50";
        ctx.fillRect(cx - 9, cy - 7, 18, 16);   // surround
        ctx.fillStyle = "#1a1a1a";
        ctx.fillRect(cx - 5, cy - 3, 10, 10);   // firebox
        ctx.fillStyle = "#ff8030";
        ctx.fillRect(cx - 3, cy + 1, 3, 5);
        ctx.fillStyle = "#ffc040";
        ctx.fillRect(cx, cy - 1, 3, 6);
        ctx.fillStyle = "#ff6010";
        ctx.fillRect(cx + 2, cy + 2, 2, 4);
      } else {
        // Generic chair
        ctx.fillRect(cx - 6, cy - 1, 12, 7);
        ctx.fillRect(cx - 7, cy - 9, 2, 9);
        ctx.fillRect(cx + 5, cy - 9, 2, 9);
        ctx.fillRect(cx - 6, cy + 6, 3, 5);
        ctx.fillRect(cx + 3, cy + 6, 3, 5);
      }
    } else if (type === "clothing") {
      if (n.includes("cloak") || n.includes("cape")) {
        ctx.beginPath();
        ctx.moveTo(cx - 2, cy - 10);
        ctx.lineTo(cx - 7, cy + 8);
        ctx.quadraticCurveTo(cx, cy + 10, cx + 7, cy + 8);
        ctx.lineTo(cx + 2, cy - 10);
        ctx.closePath();
        ctx.fill();
      } else if (n.includes("crown")) {
        ctx.beginPath();
        ctx.moveTo(cx - 7, cy + 2);
        ctx.lineTo(cx - 7, cy - 3);
        ctx.lineTo(cx - 3, cy);
        ctx.lineTo(cx, cy - 6);
        ctx.lineTo(cx + 3, cy);
        ctx.lineTo(cx + 7, cy - 3);
        ctx.lineTo(cx + 7, cy + 2);
        ctx.closePath();
        ctx.fill();
      } else if (n.includes("scarf")) {
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(cx - 6, cy - 6);
        ctx.quadraticCurveTo(cx + 4, cy - 3, cx + 2, cy + 3);
        ctx.quadraticCurveTo(cx, cy + 8, cx - 3, cy + 7);
        ctx.stroke();
        ctx.lineWidth = 1.5;
      } else if (n.includes("glove")) {
        ctx.beginPath();
        ctx.moveTo(cx - 4, cy - 8);
        ctx.lineTo(cx + 4, cy - 8);
        ctx.lineTo(cx + 4, cy + 6);
        ctx.quadraticCurveTo(cx, cy + 9, cx - 4, cy + 6);
        ctx.closePath();
        ctx.fill();
        // Thumb
        ctx.beginPath();
        ctx.ellipse(cx + 6, cy - 1, 2, 4, 0.3, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Generic shirt
        ctx.beginPath();
        ctx.moveTo(cx, cy - 10);
        ctx.lineTo(cx - 8, cy - 6);
        ctx.lineTo(cx - 6, cy - 2);
        ctx.lineTo(cx - 5, cy + 8);
        ctx.lineTo(cx + 5, cy + 8);
        ctx.lineTo(cx + 6, cy - 2);
        ctx.lineTo(cx + 8, cy - 6);
        ctx.lineTo(cx, cy - 10);
        ctx.fill();
      }
    } else if (type === "material") {
      if (n.includes("wood") || n.includes("oak")) {
        // Log rounds
        ctx.beginPath();
        ctx.arc(cx, cy, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = color + "40";
        ctx.beginPath();
        ctx.arc(cx, cy, 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(cx, cy, 2, 0, Math.PI * 2);
        ctx.stroke();
      } else if (n.includes("thread") || n.includes("silver")) {
        ctx.fillRect(cx - 2, cy - 7, 4, 14);
        ctx.beginPath();
        ctx.ellipse(cx, cy - 7, 5, 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(cx, cy + 7, 5, 2, 0, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Crystal/gem
        ctx.beginPath();
        ctx.moveTo(cx, cy - 9);
        ctx.lineTo(cx + 7, cy - 2);
        ctx.lineTo(cx + 5, cy + 7);
        ctx.lineTo(cx - 5, cy + 7);
        ctx.lineTo(cx - 7, cy - 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
    } else {
      // Star / collectible
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i * Math.PI * 2) / 5 - Math.PI / 2;
        const b = a + Math.PI / 5;
        ctx.lineTo(cx + Math.cos(a) * 8, cy + Math.sin(a) * 8);
        ctx.lineTo(cx + Math.cos(b) * 4, cy + Math.sin(b) * 4);
      }
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  /**
   * Tab pill — amber-on-wood when active, dim otherwise.
   */
  private drawTab(x: number, y: number, w: number, h: number, label: string, active: boolean): void {
    const { ctx } = this;
    ctx.save();
    this.rr(x, y, w, h, 4);
    if (active) {
      ctx.fillStyle = W.goldMid;
      ctx.fill();
      ctx.strokeStyle = W.goldBright;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.font = "bold 11px monospace";
      ctx.fillStyle = W.woodDark;
    } else {
      ctx.fillStyle = "rgba(20,14,6,0.85)";
      ctx.fill();
      ctx.strokeStyle = W.goldDark;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.font = "11px monospace";
      ctx.fillStyle = W.textDim;
    }
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + w / 2, y + h / 2);
    ctx.restore();
  }

  /**
   * Close × button (red-tinted).
   */
  private drawCloseButton(x: number, y: number, sz: number): void {
    const { ctx } = this;
    ctx.save();
    this.rr(x, y, sz, sz, 4);
    ctx.fillStyle = "#2a0e0e";
    ctx.fill();
    ctx.strokeStyle = "#6a2018";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.font = "bold 13px monospace";
    ctx.fillStyle = "#e08060";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("✕", x + sz / 2, y + sz / 2);
    ctx.restore();
  }

  // ─── Hit Zone Helpers ────────────────────────────────────────────────────────

  private addHit(x: number, y: number, w: number, h: number, fn: (s: GameState) => void): void {
    this.hitZones.push({ x, y, w, h, action: fn });
  }

  /** Allow the room renderer to register hit zones (e.g. furniture toolbar buttons). */
  addHitFromRoom(x: number, y: number, w: number, h: number, fn: (s: GameState) => void): void {
    this.roomHitZones.push({ x, y, w, h, action: fn });
  }

  // ─── Zone Banner (top center) ──────────────────────────────────────────────

  private drawZoneBanner(state: GameState, SW: number): void {
    const zoneId = state.world.currentZoneId;
    if (!zoneId || zoneId === "ember_lane") return;

    const zone = getZone(state.world, zoneId);
    if (!zone) return;

    const { ctx } = this;
    const text = zone.name;

    // ── Entrance animation: detect zone change ──
    if (text !== this.lastZoneName) {
      this.lastZoneName = text;
      this.zoneEntryTime = Date.now();
    }

    const ANIM_DURATION = 600; // ms
    const elapsed = Date.now() - this.zoneEntryTime;
    const animT = Math.min(1, elapsed / ANIM_DURATION);
    // Ease-out cubic: decelerating toward 1
    const ease = 1 - Math.pow(1 - animT, 3);

    // Compute alpha and Y offset from animation
    const animAlpha = ease;
    const animYOff = (1 - ease) * -40;

    // Measure text to size the pill
    ctx.save();
    ctx.font = "bold 14px monospace";
    const tm = ctx.measureText(text);
    const textW = tm.width;
    ctx.restore();

    const padX = 20;
    const dotSize = 6;
    const dotGap = 4;
    const dangerDotsW = zone.dangerLevel > 0
      ? zone.dangerLevel * (dotSize + dotGap) + 8
      : 0;
    const pillW = textW + padX * 2 + dangerDotsW;
    const pillH = 32;
    const pillX = (SW - pillW) / 2;
    // Offset down when a mode banner occupies the top center
    const modeBannerVisible = state.ui.editMode || state.ui.combatStance || (state.ui.placingFurnitureType != null) || (state.ui.movingFurnitureId != null);
    const bannerOffset = modeBannerVisible ? 44 : 0;
    const pillY = 14 + animYOff + bannerOffset;
    const pillR = 10;

    // Apply entrance animation alpha
    ctx.save();
    ctx.globalAlpha = animAlpha;

    // Wood panel pill
    this.drawWoodPanel(pillX, pillY, pillW, pillH, pillR);

    // Accent stripe (zone primary color) along bottom edge
    ctx.save();
    this.rr(pillX, pillY, pillW, pillH, pillR);
    ctx.clip();
    ctx.fillStyle = zone.primaryColor;
    ctx.globalAlpha = animAlpha * 0.25;
    ctx.fillRect(pillX, pillY + pillH - 4, pillW, 4);
    ctx.restore();

    // Zone name text (gold)
    ctx.save();
    ctx.globalAlpha = animAlpha;
    ctx.font = "bold 14px monospace";
    ctx.fillStyle = W.goldGlow;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    const textCx = pillX + padX + textW / 2;
    ctx.fillText(text, textCx, pillY + pillH / 2);
    ctx.restore();

    // Danger dots
    let dotsStartX = 0;
    let dotCY = 0;
    if (zone.dangerLevel > 0) {
      const DANGER_COLORS = ["#50b050", "#d0c030", "#d08030", "#c03030"];
      const dotColor = DANGER_COLORS[zone.dangerLevel] ?? "#c03030";
      dotsStartX = pillX + padX + textW + 12;
      dotCY = pillY + pillH / 2;

      ctx.save();
      ctx.globalAlpha = animAlpha;
      for (let i = 0; i < zone.dangerLevel; i++) {
        const dx = dotsStartX + i * (dotSize + dotGap);
        ctx.beginPath();
        ctx.arc(dx + dotSize / 2, dotCY, dotSize / 2, 0, Math.PI * 2);
        ctx.fillStyle = dotColor;
        ctx.fill();
        // Subtle glow ring
        ctx.strokeStyle = dotColor;
        ctx.globalAlpha = animAlpha * 0.4;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.globalAlpha = animAlpha;
      }
      ctx.restore();
    }

    // Restore from the entrance animation alpha wrapper
    ctx.restore();

    // ── Danger dots hover tooltip ──
    if (zone.dangerLevel > 0 && animT >= 1) {
      // Hit area covers all danger dots with ~20px margin
      const dotsW = zone.dangerLevel * (dotSize + dotGap);
      const hitX = dotsStartX - 10;
      const hitY = dotCY - 10;
      const hitW = dotsW + 20;
      const hitH = 20;

      if (
        this.mouseX >= hitX && this.mouseX <= hitX + hitW &&
        this.mouseY >= hitY && this.mouseY <= hitY + hitH
      ) {
        const DANGER_LABELS = ["", "Danger: Low", "Danger: Moderate", "Danger: High", "Danger: Extreme"];
        const label = DANGER_LABELS[zone.dangerLevel] ?? "Danger: Extreme";
        this.tooltipZones.push({
          x: dotsStartX,
          y: dotCY - dotSize / 2,
          w: dotsW,
          h: dotSize,
          text: label,
        });
      }
    }
  }

  // ─── Click Ripple Effect ────────────────────────────────────────────────

  /** Expanding gold ring that fades on every HUD button click. */
  private drawClickRipples(): void {
    const { ctx } = this;
    const now = Date.now();
    const DURATION = 350; // ms

    // Clean up expired ripples
    this.clickRipples = this.clickRipples.filter(r => now - r.time < DURATION);

    for (const r of this.clickRipples) {
      const t = (now - r.time) / DURATION; // 0→1
      const ease = 1 - Math.pow(1 - t, 2); // ease-out quad
      const radius = 6 + ease * 22;
      const alpha = (1 - t) * 0.35;

      ctx.save();
      ctx.beginPath();
      ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = W.goldBright;
      ctx.globalAlpha = alpha;
      ctx.lineWidth = 2 - t * 1.5;
      ctx.stroke();

      // Inner glow
      if (t < 0.4) {
        const innerAlpha = (1 - t / 0.4) * 0.12;
        const ig = ctx.createRadialGradient(r.x, r.y, 0, r.x, r.y, radius * 0.6);
        ig.addColorStop(0, `rgba(240, 200, 96, ${innerAlpha})`);
        ig.addColorStop(1, "rgba(240, 200, 96, 0)");
        ctx.globalAlpha = 1;
        ctx.fillStyle = ig;
        ctx.beginPath();
        ctx.arc(r.x, r.y, radius * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }
  }

  // ─── Ambient HUD Tint — the UI breathes with the world ──────────────────

  /**
   * Subtle, barely-perceptible time-of-day colour shift over the entire HUD.
   * The effect is so gentle you feel it more than see it.
   * Night: cool blue mist on panel edges. Sunset/sunrise: warm amber deepening.
   * Combat: tension — faint red warmth creeps in from edges.
   */
  private drawAmbientTint(state: GameState, _SW: number, _SH: number): void {
    const { ctx } = this;
    const gh = state.time.gameHour;

    // Time-of-day tint — only affects the action bar, compass, and minimap areas
    // Barely visible: alpha 0.02-0.06. Just enough to feel alive.
    let tintR = 0, tintG = 0, tintB = 0, tintA = 0;

    if (gh >= 20 || gh < 5) {
      // Deep night (8pm–5am): cool blue moonlight tint
      tintR = 40; tintG = 60; tintB = 120;
      tintA = 0.035;
    } else if (gh >= 5 && gh < 7) {
      // Sunrise (5am–7am): warm amber-pink glow
      const t = (gh - 5) / 2; // 0→1
      tintR = 200; tintG = 120 + t * 40; tintB = 60;
      tintA = 0.04 * (1 - t); // fades as dawn completes
    } else if (gh >= 17 && gh < 20) {
      // Sunset (5pm–8pm): deepening amber-red warmth
      const t = (gh - 17) / 3; // 0→1
      tintR = 180 + t * 40; tintG = 100 - t * 30; tintB = 30;
      tintA = 0.02 + t * 0.03;
    }
    // Daytime (7am–5pm): no tint — clean gold palette speaks for itself

    if (tintA > 0.005) {
      ctx.save();
      ctx.globalCompositeOperation = "multiply";
      ctx.globalAlpha = tintA;
      ctx.fillStyle = `rgb(${tintR},${tintG},${tintB})`;
      // Only tint the HUD edges: action bar area + corners
      // Bottom action bar region
      ctx.fillRect(0, _SH - 90, _SW, 90);
      // Top corners (compass + minimap)
      ctx.fillRect(0, 0, 130, 130);          // compass area
      ctx.fillRect(_SW - 130, 0, 130, 130);  // minimap area
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // Combat tension tint — when actively fighting, subtle red warmth seeps in
    if (state.player.combatState.active) {
      const pulse = 0.015 + Math.sin(Date.now() * 0.003) * 0.008;
      ctx.save();
      ctx.globalCompositeOperation = "multiply";
      ctx.globalAlpha = pulse;
      ctx.fillStyle = "rgb(200,60,40)";
      ctx.fillRect(0, _SH - 90, _SW, 90);  // action bar area
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.restore();
    }
  }

  // ─── Travel Home Button (top-right, near minimap) ─────────────────────────

  private drawTravelHomeButton(state: GameState, SW: number): void {
    const zoneId = state.world.currentZoneId;
    if (!zoneId || zoneId === "ember_lane" || state.world.travelState.active) {
      this.travelHomeRect = null;
      return;
    }

    const { ctx } = this;
    const btnSize = 36;
    // Position left of the minimap circle (minimap center: SW - 16 - MINIMAP_R)
    const btnX = SW - 16 - MINIMAP_R * 2 - 18 - btnSize;
    const btnY = 18;
    const btnR = 8;

    this.travelHomeRect = { x: btnX, y: btnY, w: btnSize, h: btnSize };

    // Check hover state
    const hovered =
      this.mouseX >= btnX && this.mouseX <= btnX + btnSize &&
      this.mouseY >= btnY && this.mouseY <= btnY + btnSize;

    // Warm amber glow behind button when hovered
    if (hovered) {
      ctx.save();
      const glow = ctx.createRadialGradient(
        btnX + btnSize / 2, btnY + btnSize / 2, btnSize * 0.2,
        btnX + btnSize / 2, btnY + btnSize / 2, btnSize * 0.8
      );
      glow.addColorStop(0, "rgba(240, 200, 96, 0.35)");
      glow.addColorStop(1, "rgba(240, 200, 96, 0)");
      ctx.fillStyle = glow;
      ctx.fillRect(btnX - 8, btnY - 8, btnSize + 16, btnSize + 16);
      ctx.restore();
    }

    // Wood panel background
    this.drawWoodPanel(btnX, btnY, btnSize, btnSize, btnR);

    // Brighter border on hover
    if (hovered) {
      ctx.save();
      this.rr(btnX, btnY, btnSize, btnSize, btnR);
      ctx.strokeStyle = W.goldGlow;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }

    // House silhouette icon (simple canvas paths)
    const cx = btnX + btnSize / 2;
    const cy = btnY + btnSize / 2;
    const s = 8; // half-size of the house icon

    ctx.save();
    ctx.fillStyle = hovered ? W.goldGlow : W.goldBright;
    ctx.strokeStyle = hovered ? W.goldGlow : W.goldBright;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";

    // Roof (triangle)
    ctx.beginPath();
    ctx.moveTo(cx, cy - s);           // apex
    ctx.lineTo(cx - s - 2, cy - 1);   // left eave
    ctx.lineTo(cx + s + 2, cy - 1);   // right eave
    ctx.closePath();
    ctx.fill();

    // House body (rectangle)
    ctx.fillRect(cx - s + 1, cy - 1, s * 2 - 2, s + 2);

    // Door (dark cutout)
    ctx.fillStyle = W.woodDark;
    ctx.fillRect(cx - 2, cy + 2, 4, s);

    // Window (small square, amber glow)
    ctx.fillStyle = "rgba(240, 200, 96, 0.7)";
    ctx.fillRect(cx + 3, cy + 1, 3, 3);
    ctx.restore();

    // Tooltip on hover
    if (hovered) {
      this.tooltipZones.push({
        x: btnX,
        y: btnY,
        w: btnSize,
        h: btnSize,
        text: "Return to Ember Lane",
      });
    }
  }

  // ─── Test Zone Button (below minimap) ────────────────────────────────────

  private drawTestZoneButton(state: GameState, SW: number): void {
    if (state.scene !== "room" || !state.currentRoom) {
      this.testZoneRect = null;
      return;
    }

    const { ctx } = this;
    const btnSize = 36;
    const isTestZone = state.currentRoom.id === "room_test_zone";

    // Position: left of minimap, below the travel-home button if visible, else same row
    const travelBtnExists = this.travelHomeRect !== null;
    const travelBtnX = SW - 16 - MINIMAP_R * 2 - 18 - btnSize; // same X as travel-home
    const btnX = travelBtnExists ? travelBtnX : travelBtnX;
    const btnY = travelBtnExists ? 18 + btnSize + 8 : 18; // stack below travel-home, or same spot
    const btnR = 8;

    this.testZoneRect = { x: btnX, y: btnY, w: btnSize, h: btnSize };

    const hovered =
      this.mouseX >= btnX && this.mouseX <= btnX + btnSize &&
      this.mouseY >= btnY && this.mouseY <= btnY + btnSize;

    // Soft glow behind button when hovered
    if (hovered) {
      ctx.save();
      const glow = ctx.createRadialGradient(
        btnX + btnSize / 2, btnY + btnSize / 2, btnSize * 0.2,
        btnX + btnSize / 2, btnY + btnSize / 2, btnSize * 0.8
      );
      if (isTestZone) {
        glow.addColorStop(0, "rgba(96, 200, 240, 0.35)");
        glow.addColorStop(1, "rgba(96, 200, 240, 0)");
      } else {
        glow.addColorStop(0, "rgba(240, 200, 96, 0.35)");
        glow.addColorStop(1, "rgba(240, 200, 96, 0)");
      }
      ctx.fillStyle = glow;
      ctx.fillRect(btnX - 8, btnY - 8, btnSize + 16, btnSize + 16);
      ctx.restore();
    }

    // Wood panel background
    this.drawWoodPanel(btnX, btnY, btnSize, btnSize, btnR);

    // Active state: subtle persistent glow when in the test zone
    if (isTestZone) {
      ctx.save();
      this.rr(btnX, btnY, btnSize, btnSize, btnR);
      ctx.strokeStyle = "rgba(96, 200, 240, 0.6)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    } else if (hovered) {
      ctx.save();
      this.rr(btnX, btnY, btnSize, btnSize, btnR);
      ctx.strokeStyle = W.goldGlow;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }

    // Flask/beaker icon — a simple test tube shape
    const cx = btnX + btnSize / 2;
    const cy = btnY + btnSize / 2;
    const iconColor = isTestZone
      ? (hovered ? "#90e0ff" : "#70c0e0")
      : (hovered ? W.goldGlow : W.goldBright);

    ctx.save();
    ctx.strokeStyle = iconColor;
    ctx.fillStyle = iconColor;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Flask neck (narrow tube at top)
    ctx.beginPath();
    ctx.moveTo(cx - 2, cy - 8);
    ctx.lineTo(cx - 2, cy - 2);
    // Flask body (widens out into a rounded bottom)
    ctx.lineTo(cx - 7, cy + 5);
    ctx.quadraticCurveTo(cx - 7, cy + 9, cx, cy + 9);
    ctx.quadraticCurveTo(cx + 7, cy + 9, cx + 7, cy + 5);
    ctx.lineTo(cx + 2, cy - 2);
    ctx.lineTo(cx + 2, cy - 8);
    ctx.stroke();

    // Liquid fill (lower third of flask)
    ctx.beginPath();
    ctx.moveTo(cx - 5, cy + 2);
    ctx.lineTo(cx - 7, cy + 5);
    ctx.quadraticCurveTo(cx - 7, cy + 9, cx, cy + 9);
    ctx.quadraticCurveTo(cx + 7, cy + 9, cx + 7, cy + 5);
    ctx.lineTo(cx + 5, cy + 2);
    ctx.closePath();
    ctx.globalAlpha = 0.4;
    ctx.fill();
    ctx.globalAlpha = 1;

    // Stopper / rim at top
    ctx.beginPath();
    ctx.moveTo(cx - 3, cy - 8);
    ctx.lineTo(cx + 3, cy - 8);
    ctx.stroke();

    // Bubbles (tiny circles inside liquid when in test zone — alive indicator)
    if (isTestZone) {
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      const t = Date.now() * 0.002;
      ctx.beginPath();
      ctx.arc(cx - 2, cy + 5 + Math.sin(t) * 1.5, 1.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx + 2, cy + 3 + Math.sin(t + 2) * 1.5, 0.8, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Tooltip on hover
    if (hovered) {
      this.tooltipZones.push({
        x: btnX,
        y: btnY,
        w: btnSize,
        h: btnSize,
        text: isTestZone
          ? "Return to your room (T)\nLeave the Asset Test Zone"
          : "Asset Test Zone (T)\nPreview pipeline-generated furniture",
      });
    }
  }

  private rr(x: number, y: number, w: number, h: number, r: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y,     x + w, y + r,     r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h,     x, y + h - r,     r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y,         x + r, y,         r);
    ctx.closePath();
  }
}
